import { buildModeledProjectBudgetEvidence, type FrozenModeledProjectBudgetSource, type ModeledProjectBudgetEvidence } from './modeledProjectBudget';
import { buildModeledAnnualFinancialMetrics, type ModeledAnnualFinancialMetricsEvidence } from './modeledAnnualFinancialMetrics';
import { buildCustomBriefAnnualFinancialMetrics, type CustomBriefAnnualFinancialMetricsEvidence } from './customBriefAnnualFinancialMetrics';
import type { ProjectIntent, ProjectPhase, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { BomItem } from '../../types/supplyChain';
import { buildSourceStatedDesignLoadMargins, type FrozenDesignLoadMarginSources, type SourceStatedDesignLoadMargin } from './sourceStatedDesignLoadMargin';
import { buildModeledGridImportMargins, type FrozenGridPlanningSources, type ModeledGridImportMargin } from './modeledGridImportMargin';
import { extractNumberedRefIds, NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import { buildSourceFinancialPlanningEnergyPrices, type SourceFinancialPlanningEnergyPrice } from './sourceFinancialPlanningEnergyPrice';
import { isGeneratorQueueBenchmarkFraming } from './numericAssertionRole';

/** Frozen source bindings for numeric roles outside the site's scalar facts.
 * A citation label alone cannot authorize an exemption: identity, source text,
 * quantity and role must all agree. This never changes the fact store. */
export interface NumericSourceEvidence {
  /** Exact transformer labels from frozen manual/uploaded BOM rows. This
   * admits the recorded equipment name, never system load or installed duty. */
  bomNameplateLabels?: readonly { itemId: string; label: string; quantity: number; provenance: 'manual' | 'uploaded-bom' }[];
  /** Closed producer statements from a separately verified frozen advisory. */
  campusAdvisoryStatements?: readonly string[];
  refs: ReadonlyMap<number, { kind: 'document' | 'knowledge'; text: string }>;
  excerpts: readonly { text: string; refIds: readonly number[] }[];
  /** Source-bound research context, never measured or verified site distance. */
  tribalHeadquartersDistances?: readonly { refId: number; entity: string; value: number; unit: 'miles' | 'km' }[];
  /** Identity comes from a frozen owner target period, never a report's value. */
  phaseTargets?: readonly { phaseSequence: number; year: number; quarter?: number }[];
  /** Exact ledger copies retain their row's phase identity, not its value. The
   * grounding validator must still check the canonical phase quantity/basis. */
  phaseClaims?: readonly { text: string; phaseSequence: number; sourceId: string; sectionId?: string; refIds?: readonly number[] }[];
  /** Battery ratings recorded verbatim in the frozen owner phase notes
   * ("BESS <P> MW discharge / <E> MWh"). Owner intent is authoritative
   * evidence of the proposal's rating; it never establishes installed,
   * committed or campus-load capacity. */
  phaseEquipment?: readonly { phaseSequence: number; kind: 'bess'; powerMw: number; energyMwh: number }[];
  /** A serialized BOP claim retains only the refs on its exact current
   * rendered sentence. An arbitrary claim/source pair is not authority. */
  annualFacilityDutyClaims?: readonly { text: string; refIds: readonly number[] }[];
  /** Literal reserve comparisons from the report's selected raw documents.
   * These never establish measured utility/grid headroom or derive a margin. */
  reserveHeadroomComparisons?: readonly { refId: number; reservePct: number; targetMw: number; headroomMw: number }[];
  /** Literal current-document load-service allocations, distinct from
   * unquantified incremental grid headroom and aggregate feeder ratings. */
  loadServiceAllocations?: readonly { refId: number; allocationMw: number }[];
  /** Own equipment schedule cells validated against the current frozen SCM
   * snapshot. A later OEM queue label never makes this slack a grid queue. */
  equipmentSlackCells?: readonly { prefix: string; slack: string }[];
  /** Exact frozen fictional normal-case import/load/margin tuples, not CFS. */
  modeledGridImportMargins?: readonly ModeledGridImportMargin[];
  /** Literal ordinary full-source design comparison, never utility headroom. */
  sourceStatedDesignLoadMargins?: readonly SourceStatedDesignLoadMargin[];
  /** Saved annual-model estimate, independent of generation/PPA FA scalars. */
  modeledProjectBudget?: ModeledProjectBudgetEvidence;
  /** Financial-only, explicitly estimated annual-engine metric roles. */
  modeledAnnualFinancialMetrics?: ModeledAnnualFinancialMetricsEvidence;
  /** Same-source quotation of an accepted current Financial annual model. */
  customBriefAnnualFinancialMetrics?: CustomBriefAnnualFinancialMetricsEvidence;
  /** Exact estimated owner energy-price input; never an issued tariff or PPA. */
  financialPlanningEnergyPrices?: readonly SourceFinancialPlanningEnergyPrice[];
  /** The SED's own context-only proximity queue screen (HIFLD substation
   * capacity class), bound to the exact frozen grid reference rows. Built only
   * for basis `proximity-heuristic`; it is never this site's load-service
   * queue and never a generator-population statistic. */
  contextOnlyQueueEstimate?: { lowYears: number; highYears: number; label: string; basis: 'proximity-heuristic'; refIds: readonly number[] };
  /** Hosting / headroom capacity per named asset kV, parsed from live
   * power.grid site-context claim spans (never `claimedValue`). A developer
   * statement about one line, not utility-confirmed or site capacity. */
  developerGridCapacityStatements?: readonly { valueMw: number; assetKv: number; span: string }[];
  /** First word of the frozen grid utility name. A clause in which the utility
   * is the subject of an attribution verb is not a developer statement. */
  gridUtilityToken?: string;
  /** Real/nominal LCOE values (`USD/MWh`) recorded in `report.pathwayLcoeAtGen`. */
  pathwayLcoeValues?: readonly number[];
}

const phaseProseKey = (text: string): string => text.replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, NUMBERED_REF_TOKEN_RE.flags), '')
  .replace(/\[sed-section:[^\]]+\]/gi, '')
  .replace(/\s+([.,;:!?])/g, '$1').replace(/\s+/g, ' ').trim();

const proseRefIds = (text: string): number[] => extractNumberedRefIds(text).sort((a, b) => a - b);

const annualDutyRefIds = (text: string): number[] => extractNumberedRefIds(text).sort((a, b) => a - b);
const annualDutyProseKey = (text: string): string => text.replace(/<!--[^]*?-->/g, '')
  .replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, NUMBERED_REF_TOKEN_RE.flags), '')
  .replace(/\s+([.,;:!?])/g, '$1').replace(/\s+/g, ' ').trim();

export function buildNumericSourceEvidence(args: {
  references?: readonly { field?: string; source?: string; url?: string; documentId?: string; retrievedAt?: string; qualification?: string; sourceGrade?: string; demonstrationDisclosure?: unknown }[];
  documents?: readonly { docId?: string; textContent?: string; pages?: readonly { text?: string }[] }[];
  knowledge?: readonly { id: string; topic: string; source: string; findings: string }[];
  report?: unknown;
  /** Passed independently by the producer; never inferred from report prose. */
  frozenBomItems?: readonly Pick<BomItem, 'id' | 'name' | 'quantity' | 'derivedFrom' | 'phaseId'>[];
  frozenProjectIntent?: Pick<ProjectIntent, 'phases'> | null;
  frozenSiteResearch?: SiteEvidenceDocument['siteResearch'] | null;
  frozenSupplyChainSource?: { siteId: string; sed: SiteEvidenceDocument };
  frozenGridPlanningSources?: FrozenGridPlanningSources;
  frozenDesignLoadMarginSources?: FrozenDesignLoadMarginSources;
  frozenModeledProjectBudgetSource?: FrozenModeledProjectBudgetSource;
  /** Ordinary producer-selected current siblings, independently frozen. */
  frozenCustomBriefSourceReports?: readonly SiteRagReport[];
  campusAdvisoryStatements?: readonly string[];
  /** The frozen `sed.grid` section: its source identity and queue screen basis. */
  frozenGridEvidence?: FrozenGridEvidence | null;
  /** The store's own eligibility-filtered live claims (`liveSiteContextClaims`). */
  frozenSiteContextClaims?: readonly unknown[];
}): NumericSourceEvidence {
  const refs = new Map<number, { kind: 'document' | 'knowledge'; text: string }>();
  for (const [index, ref] of (args.references ?? []).entries()) {
    if (ref.field === 'documents' && ref.documentId && ref.url === `doc://${ref.documentId}`) {
      const doc = args.documents?.find(d => d.docId === ref.documentId);
      // Raw extraction only. A model summary is not independent source proof.
      const text = doc && [doc.textContent, ...(doc.pages ?? []).map(p => p.text)].filter(Boolean).join('\n');
      if (text) refs.set(index + 1, { kind: 'document', text });
    } else {
      // DAM-compiled knowledge refs carry `field: 'kb-<id>'`. The prefix only
      // locates the CURRENT knowledge entry; the exact source-string equality
      // still binds. A DAM compiled from a since-corrected entry carries the
      // superseded source string and therefore stays unbound — stale text is
      // never admitted as evidence.
      const entry = args.knowledge?.find(k => (k.id === ref.field || `kb-${k.id}` === ref.field)
        && ref.source === `${k.topic} — ${k.source}`);
      if (entry) refs.set(index + 1, { kind: 'knowledge', text: entry.findings });
    }
  }
  const excerpts: Array<{ text: string; refIds: number[] }> = [];
  // reportFullText flattens evidence.excerpt and its refIds onto separate lines.
  // Retain that exact structural association; never borrow a paragraph's refs.
  const visit = (value: unknown, depth: number): void => {
    if (!value || typeof value !== 'object' || depth > 10) return;
    if (Array.isArray(value)) { for (const item of value) visit(item, depth + 1); return; }
    const row = value as Record<string, unknown>;
    if (typeof row.excerpt === 'string' && Array.isArray(row.refIds)) {
      const refIds = row.refIds.filter((id): id is number => typeof id === 'number' && Number.isInteger(id) && refs.has(id));
      if (refIds.length) excerpts.push({ text: row.excerpt, refIds });
    }
    for (const [key, child] of Object.entries(row)) {
      if (!['documents', 'sedText', 'references', 'generationTelemetry'].includes(key)) visit(child, depth + 1);
    }
  };
  visit(args.report, 0);
  const equipmentSlackCells: NonNullable<NumericSourceEvidence['equipmentSlackCells']>[number][] = [];
  const frozenScm = args.frozenSupplyChainSource;
  const scmReport = args.report && typeof args.report === 'object' ? args.report as Partial<SiteRagReport> : undefined;
  const snapshot = frozenScm?.sed.supplyChainSnapshot;
  const schedule = scmReport?.supplyChainAnalysis?.longLeadVsCod;
  if (frozenScm && scmReport?.reportType === 'supply-chain-risk'
    && typeof frozenScm.siteId === 'string' && !!frozenScm.siteId
    // The internal service runs this gate before the typed coordinator stamps
    // site/version. Identity comes from the independently captured source
    // frame; an already present contradictory stamp must still refuse.
    && (scmReport.siteId === undefined || scmReport.siteId === frozenScm.siteId)
    && scmReport.sedId === frozenScm.sed.id
    && (scmReport.sedVersionUsed === undefined || scmReport.sedVersionUsed === frozenScm.sed.version)
    && Number.isInteger(frozenScm.sed.version) && frozenScm.sed.version > 0
    && snapshot?.status === 'verified' && snapshot.source === 'supply-chain-plan'
    && (snapshot.sourceGrade === undefined || snapshot.sourceGrade === 'regional-screening')
    && (snapshot.qualification === undefined || snapshot.qualification === 'screening')
    && Number.isFinite(Date.parse(snapshot.retrievedAt))
    && schedule != null
    && schedule?.codDate === frozenScm.sed.fullProjectIntent?.inServiceDate
    && /^\d{4}-\d{2}-\d{2}$/.test(schedule?.codDate ?? '')
    && typeof scmReport.supplyChainAnalysis?.criticalPathAssessment === 'string'
    && Array.isArray(snapshot.data?.topCriticalItems) && Array.isArray(schedule.items)) {
    for (const row of schedule.items) {
      if (!row || typeof row.item !== 'string' || typeof row.leadTimeMonths !== 'string'
        || /[|\n\r]/.test(row.item) || row.item.length > 200
        || typeof row.orderByDate !== 'string' || typeof row.slackVsCod !== 'string') continue;
      const sources = snapshot.data.topCriticalItems.filter(item => item?.name === row.item);
      if (sources.length !== 1) continue;
      const item = sources[0]!;
      const equipment = args.frozenBomItems?.filter(bom => bom.id === item.bomItemId && bom.name === item.name);
      if (equipment?.length !== 1) continue;
      const phases = frozenScm.sed.fullProjectIntent?.phases;
      if (Array.isArray(phases) && phases.length >= 2 && equipment[0]!.phaseId) {
        const anchors = phases.filter(phase => phase.id === equipment[0]!.phaseId);
        // A global-COD table cannot authorize float computed for another
        // phase. Missing/ambiguous phase identities also stay unbound.
        if (anchors.length !== 1 || anchors[0]!.targetInServiceDate !== schedule.codDate) continue;
      }
      if (!item.bomItemId || !Number.isFinite(item.slackMonths) || item.slackMonths == null
        || !Number.isFinite(item.leadTimeMonthsHigh) || item.leadTimeMonthsHigh <= 0
        || row.leadTimeMonths !== String(item.leadTimeMonthsHigh)) continue;
      const sign = item.slackMonths < 0 ? '-' : '';
      const magnitude = Math.abs(item.slackMonths).toFixed(1);
      const slack = `${sign}${magnitude} months`;
      // Match the displayed order date to the frozen snapshot's own schedule
      // anchor using the same conventional month as criticalPathAnalysis.
      const latestOrder = new Date(Date.parse(snapshot.retrievedAt) + item.slackMonths * (365.25 / 12) * 86_400_000);
      const expectedOrder = Date.parse(schedule.codDate!) - item.leadTimeMonthsHigh * (365.25 / 12) * 86_400_000;
      // Snapshot capture follows the critical-path calculation by a few ms.
      // One second covers that clock serialization gap, not a schedule buffer.
      if (!Number.isFinite(expectedOrder) || Math.abs(latestOrder.getTime() - expectedOrder) > 1_000) continue;
      if (!Number.isFinite(latestOrder.getTime()) || row.orderByDate !== latestOrder.toISOString().slice(0, 10)
        || row.slackVsCod !== slack) continue;
      const sentences = (scmReport.supplyChainAnalysis?.criticalPathAssessment ?? '').split(/[.!?](?=\s|$)/);
      const cited = (args.references ?? []).some((ref, index) => ref.field === 'supplyChainSnapshot'
        && ref.source === snapshot.source && ref.retrievedAt === snapshot.retrievedAt
        && ref.url === snapshot.sourceUrl
        && ref.sourceGrade === 'regional-screening' && ref.qualification === 'screening'
        && sentences.some(sentence => sentence.includes(row.item!)
          && sentence.includes(`${item.leadTimeMonthsHigh}-month lead time`)
          && sentence.includes(`${magnitude} months ${item.slackMonths! < 0 ? 'overdue' : 'slack'}`)
          && sentence.includes(schedule.codDate!)
          && proseRefIds(sentence).length === 1 && proseRefIds(sentence)[0] === index + 1));
      if (cited) equipmentSlackCells.push({
        prefix: `- Item: ${row.item} | Lead Time Months: ${row.leadTimeMonths} | Order By Date: ${row.orderByDate} | Slack Vs Cod: `,
        slack,
      });
    }
  }
  const reserveHeadroomComparisons: NonNullable<NumericSourceEvidence['reserveHeadroomComparisons']>[number][] = [];
  const loadServiceAllocations: NonNullable<NumericSourceEvidence['loadServiceAllocations']>[number][] = [];
  const selectedDocumentIds = args.report && typeof args.report === 'object'
    && Array.isArray((args.report as Record<string, unknown>).documentIds)
    ? (args.report as { documentIds: unknown[] }).documentIds.filter((id): id is string => typeof id === 'string') : [];
  for (const [index, reference] of (args.references ?? []).entries()) {
    if (reference.field !== 'documents' || !reference.documentId || reference.url !== `doc://${reference.documentId}`
      || !selectedDocumentIds.includes(reference.documentId)
      || args.documents?.filter(document => document.docId === reference.documentId).length !== 1) continue;
    const source = refs.get(index + 1);
    if (source?.kind !== 'document') continue;
    // Whole affirmative source sentences, not a summary, borrowed number or
    // arithmetic performed by the report. Conflicting same-role rows refuse.
    const rows = source.text.split(/[.!?](?=\s|$)|[;\n|]/).map(row => row.trim());
    const allocationRows = rows.filter(row => /\bload[- ]service\s+allocation\b/i.test(row));
    const allocations = allocationRows.flatMap(row => {
      const match = /^(?:The\s+)?(?:(?:project|site[- ]deliverable|source[- ]stated|site|firm|issued|offered)\s+)*load[- ]service\s+allocation\s+is\s+(\d[\d,]*(?:\.\d+)?)\s*MW$/i.exec(row);
      return match ? [Number(match[1]!.replace(/,/g, ''))] : [];
    });
    if (allocations.length && allocations.length === allocationRows.length
      && allocations.every(value => Number.isFinite(value) && value > 0 && value === allocations[0])) {
      loadServiceAllocations.push({ refId: index + 1, allocationMw: allocations[0]! });
    }
    const targets = rows.flatMap(row => {
      const match = /^The (\d[\d,]*(?:\.\d+)?) percent project planning reserve creates a (\d[\d,]*(?:\.\d+)?) MW reserve-padded target$/i.exec(row);
      return match ? [{ reservePct: Number(match[1]!.replace(/,/g, '')), targetMw: Number(match[2]!.replace(/,/g, '')) }] : [];
    });
    const comparisons = rows.flatMap(row => {
      const match = /^Surviving allocated service (\d[\d,]*(?:\.\d+)?) MW minus reserve-padded target (\d[\d,]*(?:\.\d+)?) MW gives (\d[\d,]*(?:\.\d+)?) MW reserve headroom$/i.exec(row);
      return match ? [{ allocationMw: Number(match[1]!.replace(/,/g, '')), targetMw: Number(match[2]!.replace(/,/g, '')),
        headroomMw: Number(match[3]!.replace(/,/g, '')) }] : [];
    });
    if (!targets.length || !comparisons.length
      || targets.some(row => !Number.isFinite(row.reservePct) || row.reservePct <= 0 || !Number.isFinite(row.targetMw) || row.targetMw <= 0
        || row.reservePct !== targets[0]!.reservePct || row.targetMw !== targets[0]!.targetMw)
      || comparisons.some(row => !Number.isFinite(row.allocationMw) || row.allocationMw <= 0 || !Number.isFinite(row.headroomMw) || row.headroomMw < 0
        || row.targetMw !== targets[0]!.targetMw || row.allocationMw !== comparisons[0]!.allocationMw || row.headroomMw !== comparisons[0]!.headroomMw)) continue;
    reserveHeadroomComparisons.push({ refId: index + 1, ...targets[0]!, headroomMw: comparisons[0]!.headroomMw });
  }
  const annualFacilityDutyClaims: NonNullable<NumericSourceEvidence['annualFacilityDutyClaims']>[number][] = [];
  if (args.report && typeof args.report === 'object') {
    const report = args.report as Record<string, unknown>;
    const sentences = typeof report.bopEconomics === 'string'
      ? report.bopEconomics.replace(/<!--[^]*?-->/g, '').split(/(?<=[.!?])\s+|\n+/) : [];
    for (const raw of Array.isArray(report.claims) ? report.claims : []) {
      if (!raw || typeof raw !== 'object') continue;
      const claim = raw as Record<string, unknown>;
      if (claim.sectionId !== 'bopEconomics' || typeof claim.text !== 'string' || !Array.isArray(claim.sources)
        || !claim.sources.length || !claim.sources.every(source => typeof source === 'string' && /^ref:[1-9]\d*$/.test(source))) continue;
      const refIds = [...new Set(claim.sources.map(source => Number(String(source).slice(4))))].sort((a, b) => a - b);
      if (refIds.some(id => refs.get(id)?.kind !== 'document')) continue;
      const matches = sentences.filter(sentence => annualDutyProseKey(sentence) === annualDutyProseKey(claim.text as string)
        && JSON.stringify(annualDutyRefIds(sentence)) === JSON.stringify(refIds));
      if (matches.length === 1) annualFacilityDutyClaims.push({ text: claim.text, refIds });
    }
  }
  const phaseClaims: Array<{ text: string; phaseSequence: number; sourceId: string; sectionId?: string; refIds?: number[] }> = [];
  if (args.report && typeof args.report === 'object') {
    const report = args.report as Record<string, unknown>;
    const phases = Array.isArray(report.developmentPathway) ? report.developmentPathway : [];
    for (const claim of Array.isArray(report.claims) ? report.claims : []) {
      if (!claim || typeof claim !== 'object') continue;
      const row = claim as Record<string, unknown>;
      const leaf = typeof row.sectionId === 'string' ? /^developmentPathway\[(\d+)\]\.description$/.exec(row.sectionId) : null;
      if ((row.sectionId !== 'developmentPathway' && !leaf) || !Array.isArray(row.sources) || row.sources.length !== 1
        || typeof row.sources[0] !== 'string' || typeof row.text !== 'string') continue;
      // Recover only the identity of a complete, uniquely owned phase sentence.
      // Its values still pass the canonical phase or cited raw-equipment checks.
      const addition = (row.sources[0] === 'sed:phasing'
        && /^Addition of \d[\d,]*(?:\.\d+)? MW of (?:IT|facility)[- ]load capacity\.$/i.test(row.text)
        || row.sources[0] === 'sed-section:projectIntentOverview'
        && /^This phase adds \d[\d,]*(?:\.\d+)? MW of (?:IT|facility)[- ]load capacity, bringing the campus total to \d[\d,]*(?:\.\d+)? MW with a planning target of \d{4}\.$/i.test(row.text));
      const equipment = /^The proposed power (?:solution|expansion|configuration)\b[^!?;\n]{0,250}\b\d[\d,.]*\s*MW\s*\/\s*\d[\d,.]*\s*MWh\s+BESS\b[^!?;\n]{0,150}\.$/i.test(row.text);
      const claimRefs = equipment && row.sources.every(source => typeof source === 'string' && /^ref:\d+$/.test(source))
        ? [...new Set(row.sources.map(source => Number(String(source).slice(4))))].sort((a, b) => a - b) : [];
      if (!addition && (!equipment || !claimRefs.length || claimRefs.some(id => refs.get(id)?.kind !== 'document'))) continue;
      const inlineClaimRefs = proseRefIds(row.text);
      if (equipment && inlineClaimRefs.length && JSON.stringify(inlineClaimRefs) !== JSON.stringify(claimRefs)) continue;
      const matches = phases.flatMap((phase: unknown, phaseIndex: number) => {
        if (leaf && phaseIndex !== Number(leaf[1])) return [];
        if (!phase || typeof phase !== 'object') return [];
        const record = phase as Record<string, unknown>;
        const sequence = typeof record.phase === 'string' ? /^Phase\s+([1-9]\d*)$/i.exec(record.phase)?.[1] : undefined;
        if (!sequence || typeof record.description !== 'string') return [];
        const sentences = record.description.split(/(?<=[.!?])\s+|\n+/).filter(sentence => phaseProseKey(sentence) === phaseProseKey(row.text as string));
        return sentences.length === 1 && (addition || JSON.stringify(proseRefIds(sentences[0]!)) === JSON.stringify(claimRefs)) ? [Number(sequence)] : [];
      });
      if (matches.length === 1) phaseClaims.push({ text: row.text, phaseSequence: matches[0]!, sourceId: row.sources[0], sectionId: row.sectionId as string, ...(equipment ? { refIds: claimRefs } : {}) });
    }
  }
  const bomRows: readonly Pick<BomItem, 'id' | 'name' | 'quantity' | 'derivedFrom'>[] = Array.isArray(args.frozenBomItems)
    ? args.frozenBomItems.filter(item => item && typeof item === 'object') : [];
  const bomIds = new Map<string, number>();
  for (const item of bomRows) {
    if (typeof item.id === 'string') bomIds.set(item.id, (bomIds.get(item.id) ?? 0) + 1);
  }
  const bomNameplateLabels: NonNullable<NumericSourceEvidence['bomNameplateLabels']>[number][] = bomRows.flatMap(item => {
    if (typeof item.id !== 'string' || !item.id.trim() || bomIds.get(item.id) !== 1
      || typeof item.name !== 'string' || !item.name.trim()
      || typeof item.quantity !== 'number' || !Number.isFinite(item.quantity) || item.quantity <= 0
      || (item.derivedFrom !== 'manual' && item.derivedFrom !== 'uploaded-bom')
      || !/\btransformers?\b/i.test(item.name)
      || ![...item.name.matchAll(/\b(\d[\d,]*\.?\d*)\s?MVA\b/gi)]
        .some(match => Number(match[1]!.replace(/,/g, '')) > 0)) return [];
    return [{ itemId: item.id, label: item.name, quantity: item.quantity, provenance: item.derivedFrom }];
  });
  const phaseTargets = (args.frozenProjectIntent?.phases ?? []).flatMap(recordedPhaseTarget);
  const phaseEquipment = (args.frozenProjectIntent?.phases ?? []).flatMap(recordedPhaseBatteryRatings);
  const research = args.frozenSiteResearch;
  const tribalHeadquartersDistances: NonNullable<NumericSourceEvidence['tribalHeadquartersDistances']>[number][] = [];
  if (research?.qualification === 'screening' && research.sourceGrade === 'inference' && research.source && research.retrievedAt) {
    const ids = (args.references ?? []).flatMap((ref, index) => ref.field === 'siteResearch'
      && ref.source === research.source && ref.retrievedAt === research.retrievedAt
      && ref.qualification === research.qualification && ref.sourceGrade === research.sourceGrade ? [index + 1] : []);
    for (const row of research.data?.tribalLands?.nearbyTribes ?? []) {
      const match = /^(.+?)\s*\(Tribal headquarters is located (?:approximately |about )?(\d+(?:\.\d+)?)\s+(miles|km)\b[^)]*\)\.?$/i.exec(row);
      if (!match || Number(match[2]) <= 0) continue;
      for (const refId of ids) tribalHeadquartersDistances.push({ refId, entity: match[1]!.trim(), value: Number(match[2]), unit: match[3]!.toLowerCase() as 'miles' | 'km' });
    }
  }
  const contextOnlyQueueEstimate = buildContextOnlyQueueEstimate(args.frozenGridEvidence, args.references);
  const developerGridCapacityStatements = buildDeveloperGridCapacityStatements(args.frozenSiteContextClaims);
  const gridUtilityToken = /^[A-Za-z][A-Za-z&'’-]{2,}$/.exec(args.frozenGridEvidence?.data?.utilityName?.trim().split(/\s+/)[0] ?? '')?.[0];
  const pathwayLcoeValues = Array.isArray((args.report as SiteRagReport | undefined)?.pathwayLcoeAtGen)
    ? ((args.report as SiteRagReport).pathwayLcoeAtGen ?? []).flatMap(row => {
      const vals: number[] = [];
      for (const rawVal of [row?.lcoeRealPerMwhUsd, row?.lcoeNominalPerMwhUsd]) {
        if (typeof rawVal === 'number' && Number.isFinite(rawVal) && rawVal > 0) {
          vals.push(rawVal, Math.round(rawVal * 10) / 10, Math.round(rawVal * 100) / 100);
        }
      }
      return vals;
    })
    : [];
  return { bomNameplateLabels, ...(args.campusAdvisoryStatements ? { campusAdvisoryStatements: args.campusAdvisoryStatements } : {}), refs, excerpts, phaseClaims, phaseTargets, tribalHeadquartersDistances, phaseEquipment, annualFacilityDutyClaims, reserveHeadroomComparisons, loadServiceAllocations, equipmentSlackCells,
    ...(contextOnlyQueueEstimate ? { contextOnlyQueueEstimate } : {}),
    ...(developerGridCapacityStatements.length ? { developerGridCapacityStatements } : {}),
    ...(gridUtilityToken ? { gridUtilityToken } : {}),
    ...(pathwayLcoeValues.length ? { pathwayLcoeValues: [...new Set(pathwayLcoeValues)] } : {}),
    ...(args.frozenModeledProjectBudgetSource ? { modeledProjectBudget: buildModeledProjectBudgetEvidence({ source: args.frozenModeledProjectBudgetSource, report: args.report as SiteRagReport | undefined }) } : {}),
    ...(args.frozenModeledProjectBudgetSource ? { modeledAnnualFinancialMetrics: buildModeledAnnualFinancialMetrics({ source: args.frozenModeledProjectBudgetSource,
      report: args.report as SiteRagReport | undefined, reportType: (args.report as SiteRagReport | undefined)?.reportType }) } : {}),
    ...((args.report as SiteRagReport | undefined)?.reportType === 'custom-brief' ? {
      customBriefAnnualFinancialMetrics: buildCustomBriefAnnualFinancialMetrics({ source: args.frozenModeledProjectBudgetSource,
        report: args.report as SiteRagReport, acceptedCurrentReports: args.frozenCustomBriefSourceReports }),
    } : {}),
    ...(args.frozenDesignLoadMarginSources ? { sourceStatedDesignLoadMargins: buildSourceStatedDesignLoadMargins({ references: args.references, sources: args.frozenDesignLoadMarginSources }) } : {}),
    ...(args.frozenDesignLoadMarginSources ? { financialPlanningEnergyPrices: buildSourceFinancialPlanningEnergyPrices({ references: args.references,
      selectedDocumentIds, sources: args.frozenDesignLoadMarginSources }) } : {}),
    ...(args.frozenGridPlanningSources ? { modeledGridImportMargins: buildModeledGridImportMargins({ references: args.references, sources: args.frozenGridPlanningSources }) } : {}) };
}

/** A copied transformer model/nameplate retains its equipment role only
 * inside the complete, exact frozen label. A separate same-value system claim,
 * altered model/rating, quantity multiplier or unbound report label gets no
 * authority here. No capacity or equipment qualification is derived. */
export function isFrozenBomNameplateMention(args: {
  text: string; index: number; unit: string; evidence?: NumericSourceEvidence;
}): boolean {
  if (args.unit !== 'MVA' || !Number.isInteger(args.index) || args.index < 0) return false;
  const mention = /^(\d[\d,]*\.?\d*)\s?MVA\b/i.exec(args.text.slice(args.index));
  if (!mention) return false;
  const end = args.index + mention[0]!.length;
  for (const row of args.evidence?.bomNameplateLabels ?? []) {
    let start = args.text.indexOf(row.label);
    while (start !== -1 && start <= args.index) {
      const labelEnd = start + row.label.length;
      if (args.index >= start && end <= labelEnd
        && !/[a-z0-9_]/i.test(args.text[start - 1] ?? '')
        && !/[a-z0-9_]/i.test(args.text[labelEnd] ?? '')) return true;
      start = args.text.indexOf(row.label, start + 1);
    }
  }
  return false;
}


/** Exact per-transformer roles in a cited raw document are separate from
 * facility apparent-power demand. This admits the recorded rating only;
 * installed/system capacity, duty, commitments and arithmetic stay governed. */
const COMPONENT_LIST_INTRODUCER_RE = /\b(?:(?:items?|components?|equipment)\s+(?:like|such\s+as)|such\s+as|including|notably)\s*$/i;
// A list introducer cannot discard another explicit component identity in
// the same clause. Such antecedents remain unbound rather than guessed.
const COMPONENT_LIST_ANTECEDENT_IDENTITY_RE = /\b(?:from|by|supplier|vendor|manufactur\w*|brand|quantity|count|(?:group|bank|set)\s+of)\b|\b(?:primary|secondary)\s+transformers?\b|\b(?:\d+|one|two|dual|three|four|five|six|seven|eight|nine|ten)\s+(?:units?|transformers?)\b/i;

function transformerRatingAt(text: string, index: number): {
  role: 'primary' | 'secondary'; value: number; count?: number; vendor?: string; clause: string;
} | undefined {
  const mention = /^(\d[\d,]*(?:\.\d+)?)\s*MVA\b/i.exec(text.slice(index));
  const value = mention ? Number(mention[1]!.replace(/,/g, '')) : NaN;
  if (!mention || !Number.isFinite(value) || value <= 0) return undefined;
  const prior = text.slice(0, index);
  const boundaries = [...prior.matchAll(/[.!?](?=\s|$)|[;\n]/g)];
  const start = boundaries.at(-1) ? boundaries.at(-1)!.index! + 1 : 0;
  const tail = text.slice(index + mention[0].length);
  const boundary = /[.!?](?=\s|$)|[;\n]/.exec(tail);
  const end = boundary ? index + mention[0].length + boundary.index : text.length;
  const before = text.slice(start, index); const after = text.slice(index + mention[0].length, end);
  const clause = text.slice(start, end);
  // Never recover a rating from a denied/conditional/foreign proposal or
  // borrow equipment words to license a facility/aggregate MVA assertion.
  if (/\b(?:not|no|without|never|withdrawn|superseded|obsolete|alternative|alternatively|if|unless|whether|guaranteed|committed|firm)\b/i.test(clause)
    || /\b(?:another|different|other|former)\s+(?:site|project)\b/i.test(clause)
    || /\b(?:total|combined|aggregate)\b/i.test(before.slice(-70))
    || /^\s+(?:of\s+)?(?:total|combined|aggregate|facility|site|campus|system|apparent)\b/i.test(after)) return undefined;
  const prefix = /\b(primary|secondary)[- ]transformers?\s+(?:(?:capacity|rating|nameplate|are|is|specified|rated|each|as|at|of|with|has|have|a|an)\s+)*(?:(\d+|one|two|dual|three|four|five|six|seven|eight|nine|ten)\s+)?$/i.exec(before);
  const suffix = /^\s*(?:[- ]rated\s+)?(primary|secondary)[- ]transformers?\b/i.exec(after);
  const equipmentClauseRole = !prefix && !suffix && /^\s+(?:duty\s+path\b|is\s+the\s+equipment\s+nameplate\s+rating\b)/i.test(after)
    ? [...before.matchAll(/\b(primary|secondary)[- ]transformers?\b/gi)].at(-1)
    : undefined;
  if (!prefix && !suffix && !equipmentClauseRole) return undefined;
  const numbers: Record<string, number> = { one: 1, two: 2, dual: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };
  const countOf = (value: string | undefined) => value === undefined ? undefined : numbers[value.toLowerCase()] ?? Number(value);
  // A location or critical-path modifier is not a manufacturer. Remove only this trailing
  // modifier, retaining the preceding explicit vendor and equipment count.
  const originalLead = prefix ? before.slice(0, prefix.index) : before;
  const locationModifier = /\b(?:on[- ]site|onsite|critical-path)\s*$/i;
  // Do not let the modifier exception discard additional explicit identity.
  // This narrow form admits prefix count/vendor only; extra suffix identity
  // remains unbound rather than guessed from the matching rating.
  if (locationModifier.test(originalLead)
    && (/\b(?:from|by|supplier|vendor|manufacturer|quantity|count|bank\s+of|set\s+of|group\s+of)\b/i.test(after)
      || [...after.matchAll(/\b(primary|secondary)\b/gi)].some(match => match[1]!.toLowerCase() !== (prefix?.[1] ?? suffix?.[1] ?? equipmentClauseRole![1]!).toLowerCase()))) return undefined;
  if (COMPONENT_LIST_INTRODUCER_RE.test(originalLead)
    && COMPONENT_LIST_ANTECEDENT_IDENTITY_RE.test(originalLead)) return undefined;
  const leadText = originalLead
    .replace(COMPONENT_LIST_INTRODUCER_RE, '')
    .replace(locationModifier, '')
    .replace(/\b(\d+|one|two|dual|three|four|five|six|seven|eight|nine|ten)\s+(?:proposed|planned)\s+/i, '$1 ');
  const lead = equipmentClauseRole ? null : /\b(?:(\d+|one|two|dual|three|four|five|six|seven|eight|nine|ten)\s+)?([A-Za-z][A-Za-z0-9&.-]*)\s*$/i.exec(leadText);
  const ignored = /^(?:the|a|an|of|at|as|for|with|rated|rating|nameplate|new|each|basis|proposed|planned|dedicated|capacity|one|two|dual|three|four|five|six|seven|eight|nine|ten)$/i;
  const vendor = lead && !ignored.test(lead[2]!) ? lead[2]!.toLowerCase() : undefined;
  const count = equipmentClauseRole ? undefined : countOf(prefix?.[2] ?? lead?.[1]
    ?? /\b(\d+|one|two|dual|three|four|five|six|seven|eight|nine|ten)\s*$/i.exec(leadText)?.[1]);
  if (count !== undefined && (!Number.isInteger(count) || count <= 0)) return undefined;
  return { role: (prefix?.[1] ?? suffix?.[1] ?? equipmentClauseRole![1]!).toLowerCase() as 'primary' | 'secondary',
    value, ...(count !== undefined ? { count } : {}), ...(vendor ? { vendor } : {}), clause };
}

/** Transport/procurement prose can name a recorded transformer rating without
 * repeating primary/secondary. Admit only an unambiguous frozen component
 * row, never facility MVA, installed duty, a transport capacity or an unknown
 * rating. Conflicting or repeated same-rating rows remain unbound. */
function frozenTransformerLogisticsRating(args: {
  text: string; index: number; evidence: NumericSourceEvidence;
}): boolean {
  const mention = /^(\d[\d,]*(?:\.\d+)?)\s*MVA\b/i.exec(args.text.slice(args.index));
  if (!mention) return false;
  const prior = args.text.slice(0, args.index);
  const boundaries = [...prior.matchAll(/[.!?](?=\s|$)|[;\n]/g)];
  const start = boundaries.at(-1) ? boundaries.at(-1)!.index! + 1 : 0;
  const tail = args.text.slice(args.index + mention[0].length);
  const boundary = /[.!?](?=\s|$)|[;\n]/.exec(tail);
  const end = boundary ? args.index + mention[0].length + boundary.index : args.text.length;
  const before = args.text.slice(start, args.index);
  const after = args.text.slice(args.index + mention[0].length, end);
  const clause = args.text.slice(start, end);
  // The quantity itself must label the component being moved or procured.
  if (!/^\s+transformers?\b/i.test(after)
    || !/\b(?:transport|delivery|deliver|shipping|ship|heavy[- ]haul|purchase\s+orders?|procurement|turning\s+radii|bridge\s+weight\s+limits)\b/i.test(clause)
    || /\b(?:not|no|without|never|withdrawn|superseded|obsolete|alternative|alternatively|if|unless|guaranteed|committed|firm|installed|operating|operational|commissioned|energized|available|secured|approved)\b/i.test(clause)
    || (/\bwhether\b/i.test(clause) && !/\bwhether\s+(?:(?:local|access|public)\s+)?(?:roads?|bridges?|route|access)\b/i.test(before))
    || /\b(?:another|different|other|former)\s+(?:site|project)\b/i.test(clause)
    || /\b(?:total|combined|aggregate)\b/i.test(before.slice(-70))
    || /\b(?:total|combined|aggregate|facility|site|campus|system|plant|utility|service)[- ]+(?:apparent|load|demand|power|capacity|requirement|supply)\b/i.test(clause)
    || /\b(?:power|capacity|load|demand|requirement|supply)\s+(?:of|for)\s+(?:the\s+)?(?:facility|site|campus|system|plant)\b/i.test(clause)
    || /^\s+transformers?\b[^;.!?\n]*\b(?:provide|supply|deliver|produce|yield|serve|carry|capacity|duty|apparent|power|load|demand)\b/i.test(after)) return false;
  const value = Number(mention[1]!.replace(/,/g, ''));
  const ratings = (args.evidence.bomNameplateLabels ?? []).flatMap(row =>
    [...row.label.matchAll(/\b\d[\d,]*(?:\.\d+)?\s*MVA\b/gi)].flatMap(match => {
      const rating = transformerRatingAt(row.label, match.index!);
      return rating?.value === value ? [{ row, rating }] : [];
    }));
  if (ratings.length !== 1) return false;
  const source = ratings[0]!;
  if (!Number.isInteger(source.row.quantity) || source.row.quantity <= 0
    || (source.rating.count !== undefined && source.rating.count !== source.row.quantity)) return false;
  // An omitted role is not permission to contradict one stated elsewhere in
  // this component clause. Explicit identities must agree with the same row.
  const suffixRoles = [...clause.matchAll(/\b(primary|secondary)\b/gi)];
  if (suffixRoles.some(match => match[1]!.toLowerCase() !== source.rating.role)) return false;
  const suffixVendorText = clause.replace(/\bfrom\s+the\s+point\s+of\s+entry\b/gi, '');
  const suffixVendors = [...suffixVendorText.matchAll(/\b(?:from|by|supplier\s*[:=]?|vendor\s*[:=]?|manufacturer\s*[:=]?)\s+(?:(?:the|a|an)\s+)?([A-Za-z][A-Za-z0-9&.-]*)\b/gi)]
    .map(match => match[1]!.toLowerCase());
  const vendorMarker = /\b(?:from|by|supplier|vendor|manufactur\w*|brand|made|built)\b/i.exec(suffixVendorText);
  if (vendorMarker && (!suffixVendors.length || /\b(?:and|or|either)\b/i.test(suffixVendorText.slice(vendorMarker.index)))) return false;
  if (suffixVendors.some(vendor => source.rating.vendor === undefined || vendor !== source.rating.vendor)) return false;
  const words: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };
  const suffixCounts = [...clause.matchAll(/\b(?:quantity\s*[:=]?\s*|count\s*[:=]?\s*|(?:a\s+)?(?:group|bank|set)\s+of\s+)(\d+|one|two|three|four|five|six|seven|eight|nine|ten)\b|\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten)\s+(?:units?|transformers?)\b/gi)];
  const countMarkers = [...clause.matchAll(/\b(?:quantity|count|(?:group|bank|set)\s+of)\b/gi)];
  if (countMarkers.length > suffixCounts.length) return false;
  if (suffixCounts.some(match => (words[(match[1] ?? match[2]!).toLowerCase()] ?? Number(match[1] ?? match[2])) !== source.row.quantity)) return false;
  if (COMPONENT_LIST_INTRODUCER_RE.test(before)
    && COMPONENT_LIST_ANTECEDENT_IDENTITY_RE.test(before)) return false;
  const leadText = before.replace(COMPONENT_LIST_INTRODUCER_RE, '');
  const lead = /\b(?:(\d+|one|two|three|four|five|six|seven|eight|nine|ten)\s+)?([A-Za-z][A-Za-z0-9&.-]*)\s*$/i.exec(leadText);
  const ignored = /^(?:the|a|an|of|for|with|rated|rating|nameplate|new|critical-path|path|orders|one|two|three|four|five|six|seven|eight|nine|ten)$/i;
  const count = lead?.[1] ?? /\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten)\s*$/i.exec(leadText)?.[1];
  if (count !== undefined && (words[count.toLowerCase()] ?? Number(count)) !== source.row.quantity) return false;
  const vendor = lead && !ignored.test(lead[2]!) ? lead[2]!.toLowerCase() : undefined;
  return vendor === undefined || vendor === source.rating.vendor;
}

/** A component paraphrase can omit the vendor only when the separately
 * frozen BOM has exactly one rating for that primary/secondary role. This
 * binds a recorded equipment rating, not installed duty or site capacity. */
export function isFrozenBomTransformerRating(args: {
  text: string; index: number; unit: string; evidence?: NumericSourceEvidence;
}): boolean {
  if (args.unit !== 'MVA' || !Number.isInteger(args.index) || args.index < 0 || !args.evidence) return false;
  const target = transformerRatingAt(args.text, args.index);
  if (!target) return frozenTransformerLogisticsRating({ ...args, evidence: args.evidence });
  if (/\b(?:installed|operating|operational|commissioned|energized|available|secured|approved|guaranteed|committed|firm)\b/i.test(target.clause)
    || /\b(?:facility|site|campus|system|plant|utility|service)[- ]+(?:apparent|load|demand|power|capacity|requirement|supply)\b/i.test(target.clause)
    || /\b(?:power|capacity|load|demand|requirement|supply)\s+(?:of|for)\s+(?:the\s+)?(?:facility|site|campus|system|plant)\b/i.test(target.clause)) return false;
  const sources = (args.evidence.bomNameplateLabels ?? []).flatMap(row => {
    if (!Number.isInteger(row.quantity) || row.quantity <= 0) return [];
    return [...row.label.matchAll(/\b\d[\d,]*(?:\.\d+)?\s*MVA\b/gi)].flatMap(match => {
      const rating = transformerRatingAt(row.label, match.index!);
      return rating?.role === target.role ? [{ row, rating }] : [];
    });
  });
  // Two possible rows (even with equal ratings) do not establish which
  // equipment the shortened statement denotes. Never select by value alone.
  if (sources.length !== 1) return false;
  const source = sources[0]!;
  return source.rating.value === target.value
    && (source.rating.count === undefined || source.rating.count === source.row.quantity)
    && (target.count === undefined || target.count === source.row.quantity)
    && (target.vendor === undefined || target.vendor === source.rating.vendor);
}

export function isSourceCitedTransformerRating(args: {
  text: string; index: number; unit: string; evidence?: NumericSourceEvidence;
}): boolean {
  if (args.unit !== 'MVA' || !Number.isInteger(args.index) || args.index < 0 || !args.evidence) return false;
  const target = transformerRatingAt(args.text, args.index);
  if (!target) return false;
  const ids = proseRefIds(target.clause);
  const sources = ids.flatMap(id => {
    const source = args.evidence!.refs.get(id);
    return source?.kind === 'document' ? [source.text] : [];
  });
  const rows = sources.flatMap(source => [...source.matchAll(/\b\d[\d,]*(?:\.\d+)?\s*MVA\b/gi)]
    .flatMap(match => transformerRatingAt(source, match.index!) ?? []))
    .filter(row => row.role === target.role);
  // Conflicting same-role source ratings cannot license a selected value.
  return rows.length > 0 && rows.every(row => row.value === target.value
    && (target.count === undefined || row.count === target.count)
    && (target.vendor === undefined || row.vendor === target.vendor));
}

/** Read battery power/energy pairs recorded in one owner phase's notes. A
 * clause that is negated, conditional, an alternative, or names a different
 * phase supplies nothing — those do not record this phase's rating. */
function recordedPhaseBatteryRatings(phase: ProjectPhase): NonNullable<NumericSourceEvidence['phaseEquipment']> {
  if (!Number.isInteger(phase.sequence) || phase.sequence < 1) return [];
  const out: Array<NonNullable<NumericSourceEvidence['phaseEquipment']>[number]> = [];
  const quantity = '(\\d[\\d,]*(?:\\.\\d+)?)';
  const battery = '(?:BESS|battery(?:\\s+energy)?\\s+storage(?:\\s+system)?)';
  const pairs = [
    // "BESS 40 MW discharge / 80 MWh energy"
    new RegExp(`\\b${battery}\\s*[:(]?\\s*${quantity}\\s*MW\\b(?:\\s+(?:discharge|power|output|rated))?\\s*\\/\\s*${quantity}\\s*MWh\\b`, 'gi'),
    // "40 MW / 80 MWh BESS"
    new RegExp(`\\b${quantity}\\s*MW\\s*\\/\\s*${quantity}\\s*MWh\\s+${battery}\\b`, 'gi'),
  ];
  for (const clause of (phase.notes ?? '').split(/(?<=[.!?])\s+|;\s*|\n+/)) {
    if (/\b(?:no|not|without|never|removed|withdrawn|superseded|obsolete|cancell?ed|excluded|alternative|alternatively|instead|or|if|unless|whether|hypothetical)\b/i.test(clause)
      || [...clause.matchAll(/\bphase\s+(\d+)\b/gi)].some(match => Number(match[1]) !== phase.sequence)) continue;
    for (const re of pairs) {
      for (const match of clause.matchAll(re)) {
        const powerMw = Number(match[1]!.replace(/,/g, ''));
        const energyMwh = Number(match[2]!.replace(/,/g, ''));
        if (!(powerMw > 0) || !(energyMwh > 0)) continue;
        if (!out.some(row => row.powerMw === powerMw && row.energyMwh === energyMwh)) {
          out.push({ phaseSequence: phase.sequence, kind: 'bess', powerMw, energyMwh });
        }
      }
    }
  }
  return out;
}

/** Adoption or requirement wording: the clause states the rating as the
 * chosen design or a requirement, not as a proposal option. */
const BATTERY_ADOPTION_RE =
  /\b(?:selected|chosen|preferred|recommended|adopted|specified|finali[sz]ed|required|requires?|requirement|mandatory|necessary|needed|must|shall|will\s+(?:include|incorporate|install|deploy|use|have|comprise|feature|provide|be)|design\s+basis|basis\s+of\s+design|base[- ]case)\b/i;

/**
 * An uncited "<P> MW / <E> MWh BESS" inside a named owner-phase row is that
 * phase's proposed battery rating — not campus load — ONLY when:
 *  - the row identity is a serialized owner phase (`rowPhase`), and the clause
 *    names no other phase;
 *  - P and E both equal a pair recorded verbatim in that phase's frozen owner
 *    notes (a different rating, or a lone P, still falls through and blocks);
 *  - the sentence frames it as a proposal/scenario/option — an adopted
 *    "Phase 1 includes a 40 MW / 80 MWh BESS" is not admitted;
 *  - nothing in the clause adopts or requires it ("selected", "preferred",
 *    "required", "will include", "design basis", …): proposal wording
 *    elsewhere in the clause does not turn an adopted rating into a proposal;
 *  - nothing in the clause asserts delivery, commitment or installation.
 * This binds the figure to owner evidence; it never validates the proposal.
 */
export function isOwnerRecordedPhaseBatteryRating(args: {
  text: string; index: number; raw: string; value: number; unit: string; rangeLow?: number;
  evidence?: NumericSourceEvidence; rowPhase?: number;
}): boolean {
  const { text, index, raw, value, unit, rowPhase } = args;
  const recorded = args.evidence?.phaseEquipment;
  if (unit !== 'MW' || rowPhase == null || !recorded?.length) return false;
  if (args.rangeLow != null && args.rangeLow !== value) return false;
  const lineStart = text.lastIndexOf('\n', index - 1) + 1;
  const lineEnd = text.indexOf('\n', index);
  const line = text.slice(lineStart, lineEnd < 0 ? undefined : lineEnd);
  const relative = index - lineStart;
  const before = line.slice(0, relative);
  const after = line.slice(relative + raw.length);
  // Clause-local: a serialized row's previous cell or sentence cannot lend
  // its framing to this figure.
  const start = Math.max(before.lastIndexOf('. ') + 1, before.lastIndexOf(';') + 1, before.lastIndexOf('|') + 1, 0);
  const endMatch = after.match(/[.!?](?=\s|$)|[;|]/);
  const clause = line.slice(start, endMatch?.index == null ? line.length : relative + raw.length + endMatch.index);
  const pair = /^\s*\/\s*(\d[\d,]*(?:\.\d+)?)\s*MWh\s+(?:BESS|battery(?:\s+energy)?\s+storage(?:\s+system)?)\b/i.exec(after);
  if (!pair) return false;
  const energy = Number(pair[1]!.replace(/,/g, ''));
  if (!/\b(?:propos(?:al|als|ed|es|e)|scenario|option)\b/i.test(line.slice(start, relative))) return false;
  if (/\b(?:committed|secured|guaranteed|approved|confirmed|commissioned|delivered|installed|operating|operational|energi[sz]ed|firm)\b/i.test(clause)) return false;
  if (BATTERY_ADOPTION_RE.test(clause)) return false;
  if ([...clause.matchAll(/\bphase\s*(\d+)\b/gi)].some(match => Number(match[1]) !== rowPhase)) return false;
  return recorded.some(row => row.phaseSequence === rowPhase && row.kind === 'bess'
    && row.powerMw === value && row.energyMwh === energy);
}

/** Read only affirmative owner planning periods. Publication years, alternatives
 * and negated/replaced targets cannot supply phase identity. An unknown exact
 * COD does not erase a separately recorded, explicitly qualified quarter. */
function recordedPhaseTarget(phase: ProjectPhase): NonNullable<NumericSourceEvidence['phaseTargets']> {
  if (!Number.isInteger(phase.sequence) || phase.sequence < 1) return [];
  if (phase.targetInServiceDate) {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(phase.targetInServiceDate) ? new Date(`${phase.targetInServiceDate}T00:00:00Z`) : null;
    return date && Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === phase.targetInServiceDate
      ? [{ phaseSequence: phase.sequence, year: date.getUTCFullYear(), quarter: Math.floor(date.getUTCMonth() / 3) + 1 }] : [];
  }
  const clauses = (phase.notes ?? '').split(/(?<=[.!?])\s+|;\s*|\n+/);
  const periods: Array<{ year: number; quarter?: number }> = [];
  for (const clause of clauses) {
    if (!/\b(?:targets?|in-service|COD|buildout)\b/i.test(clause) || !/\b20\d{2}\b/.test(clause)) continue;
    if (/\b(?:target|schedule|date)\s+(?:is|was|has been)\s+(?:now\s+)?(?:obsolete|withdrawn|superseded|replaced|abandoned|cancelled|canceled)\b/i.test(clause)
      || /\b(?:if|unless|whether|suppose|hypothetical|alternative|alternatively|instead|or|could|might|may)\b/i.test(clause)
      || /\b(?:does? not|never|no longer)\s+targets?\b|\bnot\s+(?:(?:a|the|our|current|project)\s+)*target\b/i.test(clause)
      || [...clause.matchAll(/\bphase\s+(\d+)\b/gi)].some(match => Number(match[1]) !== phase.sequence)) return [];
    const found = [
      ...clause.matchAll(/\b(?:targets?|in-service(?: date)?(?: of)?|COD(?: of)?|buildout by)\s+(?:ultimate buildout by\s+)?(Q[1-4]\s+20\d{2}|20\d{2})\b/gi),
      ...clause.matchAll(/\b(Q[1-4]\s+20\d{2}|20\d{2})\s+(?:is|was|remains)\s+(?:(?:the|an|existing|project|planning|proposed)\s+){0,5}target\b/gi),
    ];
    if (!found.length) return [];
    for (const match of found) {
      const value = /^(?:Q([1-4])\s+)?(20\d{2})$/i.exec(match[1]!);
      if (value) periods.push({ year: Number(value[2]), ...(value[1] ? { quarter: Number(value[1]) } : {}) });
    }
  }
  const unique = [...new Map(periods.map(period => [JSON.stringify(period), period])).values()];
  return unique.length === 1 ? [{ phaseSequence: phase.sequence, ...unique[0]! }] : [];
}

/** A prospective load target can name its phase by a unique frozen period.
 * Undefined means this is not that role; an empty result means the named
 * target is ambiguous/unmatched and must not fall back to another phase. */
export function phaseTargetAt(text: string, index: number, evidence?: NumericSourceEvidence, namedPhase?: number): { phaseSequence?: number } | undefined {
  if (!evidence?.phaseTargets?.length) return undefined;
  const prefix = text.slice(Math.max(0, index - 350), index);
  const before = prefix.split(/[.!?;\n|]/).at(-1) ?? '';
  const after = text.slice(index, index + 350).split(/[.!?](?=\s|$)|[;\n|]/)[0] ?? '';
  if (!/\btargets?(?:\s+(?:is|of|for|to|delivering|serving|achieving|reaching|planned|cumulative|IT|load|demand|capacity|phase\s+\d+)){0,10}\s*$/i.test(before)) return undefined;
  const period = /^\d[\d,]*(?:\.\d+)?\s*MW\s*(?:(?:of\s+)?(?:IT|facility)[- ](?:load|demand|capacity)\s+)?(?:in|by|for)\s+(?:Q([1-4])\s+)?(20\d{2})\b/i.exec(after);
  if (!period) return undefined;
  if (/\b(?:peer|comparable|another|other project|hypothetical|alternative)\b/i.test(before)) return {};
  const year = Number(period[2]), quarter = period[1] ? Number(period[1]) : undefined;
  const matches = evidence.phaseTargets.filter(target => target.year === year
    && (quarter == null || target.quarter === quarter)
    && (namedPhase == null || target.phaseSequence === namedPhase));
  return matches.length === 1 ? { phaseSequence: matches[0]!.phaseSequence } : {};
}

/** Recover only a whole serialized ledger text cell. Ordinary prose and other
 * fields cannot borrow this structural context merely by repeating a value. */
function phaseClaimEvidenceAt(text: string, index: number, evidence?: NumericSourceEvidence): NonNullable<NumericSourceEvidence['phaseClaims']>[number] | undefined {
  const start = text.lastIndexOf('\n', index - 1) + 1;
  const end = text.indexOf('\n', index);
  const line = text.slice(start, end < 0 ? undefined : end);
  const cell = /(?:^|\|)\s*(?:-\s*)?Text:\s*([^|]+?)(?=\s*\||$)/i.exec(line);
  if (!cell) return undefined;
  const valueStart = line.indexOf(cell[1]!, cell.index);
  if (index - start < valueStart || index - start >= valueStart + cell[1]!.length) return undefined;
  const sectionId = /(?:^|\|)\s*(?:-\s*)?Section Id:\s*(developmentPathway(?:\[\d+\]\.description)?)\s*(?:\||$)/i.exec(line)?.[1];
  if (!sectionId) return undefined;
  const sourceId = /(?:^|\|)\s*(?:-\s*)?Sources:\s*([^|]+?)(?=\s*\||$)/i.exec(line)?.[1]?.trim();
  const inlineRefs = proseRefIds(cell[1]!);
  return evidence?.phaseClaims?.find(claim => phaseProseKey(claim.text) === phaseProseKey(cell[1]!) && claim.sourceId === sourceId
    && (claim.sectionId ?? 'developmentPathway') === sectionId
    && (!inlineRefs.length || JSON.stringify(inlineRefs) === JSON.stringify(claim.refIds ?? [])));
}

export function phaseClaimSequenceAt(text: string, index: number, evidence?: NumericSourceEvidence): number | undefined {
  return phaseClaimEvidenceAt(text, index, evidence)?.phaseSequence;
}

const normalize = (text: string): string => text.toLowerCase().replace(/[,]/g, '').replace(/\s+/g, ' ').trim();

/** A conditional solar land requirement is not measured site acreage. Its
 * cited acreage-per-MW benchmark establishes only the estimate's context;
 * this does not validate its arithmetic, firm capacity, or usable land. */
export function isSourceCitedSolarLandEstimate(args: {
  text: string; index: number; raw: string; evidence?: NumericSourceEvidence;
}): boolean {
  const { text, index, raw, evidence } = args;
  if (!evidence) return false;
  const prefix = text.slice(Math.max(0, index - 400), index);
  const priorBreak = Math.max(prefix.lastIndexOf('. '), prefix.lastIndexOf('\n'), prefix.lastIndexOf(';'));
  const before = prefix.slice(priorBreak < 0 ? 0 : priorBreak + 1);
  const suffix = text.slice(index + raw.length, index + raw.length + 250);
  const nextBreak = suffix.search(/[.!?](?=\s|$)|\n|;/);
  const after = suffix.slice(0, nextBreak < 0 ? undefined : nextBreak + 1);
  if (!/\bsolar(?:\s+PV|\s+generation)?\b/i.test(before)
    || !/\b(?:would|could|may|might)\s+require\b[^.;\n]{0,100}\bland\s+(?:footprint|requirement|area)\s+(?:of\s+)?(?:(?:potentially|approximately|about|roughly|estimated|over|more\s+than|at\s+least)\s+)*$/i.test(before)) return false;
  const ids = [...(before + raw + after).matchAll(/\[Ref\s+(\d+)\]/gi)].map(m => Number(m[1]));
  return ids.some(id => {
    const source = evidence.refs.get(id);
    return source?.kind === 'knowledge'
      && /\bland\s+requirement\s*:\s*\d+(?:\.\d+)?\s*[-–]\s*\d+(?:\.\d+)?\s+acres\s*\/\s*MW\s+for\s+utility-scale\s+solar\b/i.test(source.text);
  });
}

/** Keep an attributed research estimate for one tribal headquarters separate
 * from reactor/utility distances. This does not establish surveyed distance,
 * tribal territory, consultation applicability or a public sentiment finding. */
export function sourceCitedTribalHeadquartersDistance(args: {
  text: string; index: number; raw: string; evidence?: NumericSourceEvidence;
}): { matches: boolean; expected: string } | undefined {
  const { text, index, raw, evidence } = args;
  const quantity = /^(\d[\d,]*(?:\.\d+)?)\s+(miles|km)$/i.exec(raw);
  if (!quantity || !evidence?.tribalHeadquartersDistances?.length) return undefined;
  const before = text.slice(Math.max(0, index - 500), index).split(/[.!?](?=\s|$)|[;\n|]/).at(-1) ?? '';
  const after = text.slice(index + raw.length, index + raw.length + 300).split(/[.!?](?=\s|$)|[;\n|]/)[0] ?? '';
  const clause = before + raw + after;
  if (!/\bresearch\b|\bscreening\b/i.test(clause)
    || /\b(?:surveyed|measured|verified|confirmed|reservation|territory|boundary)\b/i.test(clause.replace(/\bnot\s+(?:independently\s+)?(?:verified|confirmed|measured|surveyed)\b/gi, 'unverified'))) return undefined;
  const ids = proseRefIds(clause);
  const row = evidence.tribalHeadquartersDistances.find(row => ids.includes(row.refId)
    && new RegExp(`${row.entity.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*,?\\s*(?:(?:whose|the|tribal)\\s+)*headquarters\\b[^.;!?]{0,100}$`, 'i').test(before));
  return row ? { matches: row.value === Number(quantity[1]!.replace(/,/g, '')) && row.unit === quantity[2]!.toLowerCase(),
    expected: `${row.value} ${row.unit} for ${row.entity} headquarters, as an unverified screening-research estimate` } : undefined;
}

/** Only an explicitly attributed requested phase, or an explicitly separate
 * generator statistic, can have this role. Site commitments remain governed. */
const ANNUAL_FACILITY_DUTY_ROLE = /\b(?:annual\s+(?:(?:facility|site)\s+)?(?:imported\s+)?(?:electrical|electricity)\s+(?:duty|consumption|demand)|(?:facility|site)\s+annual\s+(?:imported\s+)?(?:electrical|electricity)\s+(?:duty|consumption|demand)|selected\s+facility\s+screen\s+models\s+\d[\d,]*(?:\.\d+)?\s*(?:MWh|GWh)\s*(?:\/\s*(?:year|yr)|per\s+year|annually)\b[^;!?]*\brepresents\s+the\s+project\s+load\s+profile)\b/i;
const ANNUAL_DUTY_REJECTED = /\b(?:not|no|without|never|unknown|unavailable|withdrawn|superseded|obsolete|alternative|another|different|other|former|guaranteed|committed|firm|measured|metered|generation|generated|production|BESS|battery|storage)\b/i;

function annualDutyClause(text: string, index: number, length: number): string {
  const barrier = /[.!?](?=\s|$)|[;\n|]|\b(?:but|however|while|whereas|although|unlike|despite)\b/gi;
  const before = [...text.slice(0, index).matchAll(barrier)].at(-1);
  const start = before ? before.index! + before[0].length : 0;
  const after = new RegExp(barrier.source, 'i').exec(text.slice(index + length));
  return text.slice(start, after ? index + length + after.index : undefined);
}

/** A declared facility-consumption screen is not financial annual generation.
 * Bind only the exact annual-energy quantity and role in a cited frozen raw
 * document. No arithmetic, model result or commitment is inferred here. */
function isSourceCitedAnnualFacilityDuty(args: {
  text: string; index: number; raw: string; value: number; unit: string; rangeLow?: number;
  evidence?: NumericSourceEvidence;
}): boolean {
  const { text, index, raw, value, evidence } = args;
  if (args.unit !== 'MWh' || !evidence || !Number.isFinite(value) || value <= 0
    || (args.rangeLow != null && args.rangeLow !== value)) return false;
  const clause = annualDutyClause(text, index, raw.length)
    .replace(/^(?:\s*[-|]?\s*[A-Za-z ]+:\s*)?Data not available,\s*site-specific assessment required\s*\(/i, '');
  const maskedClause = clause.replace(
    /\brather\s+than\s+utility\s+feed\s+availability(?:,\s*requiring\s+confirmation\s+of\s+firm\s+non-curtailable\s+service\s+terms)?/gi,
    '',
  );
  if (!ANNUAL_FACILITY_DUTY_ROLE.test(clause) || !/\b(?:facility|site)\b/i.test(clause)
    || !/\b(?:assumes?|assumed|planning|screen(?:ing)?|modeled|modelled|source[- ]stated)\b/i.test(clause)
    || ANNUAL_DUTY_REJECTED.test(maskedClause)) return false;
  const inlineRefs = annualDutyRefIds(clause);
  const lineStart = text.lastIndexOf('\n', index - 1) + 1;
  const lineEnd = text.indexOf('\n', index);
  const line = text.slice(lineStart, lineEnd < 0 ? undefined : lineEnd);
  const relative = index - lineStart;
  const boundClaims = (evidence.annualFacilityDutyClaims ?? []).filter(claim => {
    const start = line.indexOf(claim.text);
    return start >= 0 && relative >= start && relative + raw.length <= start + claim.text.length;
  });
  const refIds = inlineRefs.length ? inlineRefs : boundClaims.length === 1 ? [...boundClaims[0]!.refIds] : [];
  if (!refIds.length || (inlineRefs.length && boundClaims.some(claim => JSON.stringify(claim.refIds) !== JSON.stringify(inlineRefs)))) return false;
  const sources = refIds.map(id => evidence.refs.get(id));
  if (sources.some(source => source?.kind !== 'document')) return false;
  return sources.every(source => {
    const values = [...source!.text.matchAll(/\b(\d[\d,]*(?:\.\d+)?)\s*(MWh|GWh)\s*(?:\/\s*(?:year|yr)|per\s+year|annually)\b/gi)]
      .flatMap(match => {
        const scoped = annualDutyClause(source!.text, match.index!, match[0].length);
        if (!ANNUAL_FACILITY_DUTY_ROLE.test(scoped) || !/\b(?:facility|site)\b/i.test(scoped)
          || ANNUAL_DUTY_REJECTED.test(scoped)) return [];
        return [Number(match[1]!.replace(/,/g, '')) * (match[2]!.toLowerCase() === 'gwh' ? 1000 : 1)];
      });
    return values.length > 0 && values.every(recorded => recorded === value);
  });
}

export function isSupportedSourceRoleNumber(args: {
  text: string; index: number; raw: string; value: number; unit: string; rangeLow?: number;
  evidence?: NumericSourceEvidence;
}): boolean {
  const { text, index, raw, value, unit, evidence } = args;
  if (unit === 'MWh') return isSourceCitedAnnualFacilityDuty(args);
  if (unit === 'USD/MWh') {
    const lineStart = text.lastIndexOf('\n', index - 1) + 1;
    const lineEnd = text.indexOf('\n', index);
    const line = text.slice(lineStart, lineEnd < 0 ? undefined : lineEnd);
    if (evidence?.pathwayLcoeValues?.some(v => Math.abs(v - value) <= 0.05)) {
      if (/\|\s*Lcoe:\s*\$/i.test(line)
        || /\b(?:per[- ]phase\s+finan\w*|modeled\s+Phase\s+\d+\s+[\w-]+\s+LCOE)\b/i.test(line)) {
        return true;
      }
    }
  }
  if (unit === 'USD' && evidence) {
    const lineStart = text.lastIndexOf('\n', index - 1) + 1;
    const lineEnd = text.indexOf('\n', index);
    const line = text.slice(lineStart, lineEnd < 0 ? undefined : lineEnd);
    const rel = index - lineStart;
    const splitMatch = /\(plus\s+sub-lot\s+CAPEX\s+splits\s+of\s+(\$\d[\d,]*(?:\.\d+)?M\s+vs\s+\$\d[\d,]*(?:\.\d+)?M\/\$\d[\d,]*(?:\.\d+)?M\/\$\d[\d,]*(?:\.\d+)?M\s+\[(?:verified\s+)?Ref\s+([1-9]\d*)\](?:\s*\[source passage\]\(doc:\/\/[^)]+\)|\s*\(doc:\/\/[^)]+\))?)\s+and\s+(\$\d[\d,]*(?:\.\d+)?M\s+vs\s+\$\d[\d,]*(?:\.\d+)?M\/\$\d[\d,]*(?:\.\d+)?M\/\$\d[\d,]*(?:\.\d+)?M\s+\[(?:verified\s+)?Ref\s+([1-9]\d*)\](?:\s*\[source passage\]\(doc:\/\/[^)]+\)|\s*\(doc:\/\/[^)]+\))?)\)/i.exec(line);
    if (splitMatch && rel >= splitMatch.index && rel + raw.length <= splitMatch.index + splitMatch[0].length) {
      const firstEnd = splitMatch.index + splitMatch[0].indexOf(' and ');
      const refId = Number(rel < firstEnd ? splitMatch[2] : splitMatch[4]);
      const source = evidence.refs.get(refId);
      const millions = value / 1_000_000;
      if (
        source?.kind === 'document'
        && Number.isFinite(millions)
        && millions > 0
        && new RegExp(`\\bUSD\\s+${String(millions).replace(/\./g, '\\.')}\\s+million\\b`, 'i').test(source.text)
      ) {
        return true;
      }
    }
  }
  if (!evidence || !['MW', 'years'].includes(unit)) return false;
  if (unit === 'years') {
    const start = text.lastIndexOf('\n', index - 1) + 1;
    const end = text.indexOf('\n', index);
    const line = text.slice(start, end < 0 ? undefined : end);
    if (evidence.equipmentSlackCells?.some(cell => line.startsWith(cell.prefix + cell.slack + ' | Slip Risk: ')
      && index === start + cell.prefix.length + (cell.slack.startsWith('-') ? 1 : 0)
      && raw === cell.slack.replace(/^-/, ''))) return true;
  }
  if (unit === 'MW' && (args.rangeLow == null || args.rangeLow === value)) {
    // This number labels an allocation, not the nearby unknown headroom.
    // Its own directly attached ref must bind the exact selected raw source;
    // a previous aggregate-feeder citation cannot authorize this assertion.
    const allocationAfter = /^\s+(?:(?:site[- ]deliverable|load[- ]service|service|site)\s+)*allocation\s*\[Ref\s+([1-9]\d*)\](?=[\s.,;!?]|$)/i.exec(text.slice(index + raw.length));
    const allocationClause = annualDutyClause(text, index, raw.length);
    const allocationBefore = allocationClause.slice(0, index - text.lastIndexOf(allocationClause, index));
    if (allocationAfter && /\b(?:initial|source[- ]stated|documented|offered|issued)\s+$/i.test(allocationBefore)
      && evidence.loadServiceAllocations?.some(row => row.refId === Number(allocationAfter[1]) && row.allocationMw === value)) return true;
    const clause = annualDutyClause(text, index, raw.length).trim();
    const comparison = /^(?:-\s*[A-Za-z][A-Za-z ]{0,63}:\s*)?The (\d[\d,]*(?:\.\d+)?) percent project planning reserve creates a (\d[\d,]*(?:\.\d+)?) MW reserve-padded target, leaving (\d[\d,]*(?:\.\d+)?) MW of reserve headroom under the allocated grid service(?:\s*\(doc:\/\/[^)]+\))?\s*\[(?:verified\s+)?Ref ([1-9]\d*)\]$/i.exec(clause);
    if (comparison) {
      const quantities = [...clause.matchAll(/\b\d[\d,]*(?:\.\d+)?\s*MW\b/gi)];
      const clauseStart = text.lastIndexOf(clause, index);
      // Each token retains its exact design role; neither becomes offered
      // utility capacity, facility demand or a later grid-headroom assertion.
      const role = quantities.findIndex(quantity => index === clauseStart + quantity.index);
      if (quantities.length === 2 && role >= 0
        && value === Number(comparison[role + 2]!.replace(/,/g, ''))
        && evidence.reserveHeadroomComparisons?.some(row => row.refId === Number(comparison[4])
          && row.reservePct === Number(comparison[1]!.replace(/,/g, ''))
          && row.targetMw === Number(comparison[2]!.replace(/,/g, ''))
          && row.headroomMw === Number(comparison[3]!.replace(/,/g, '')))) return true;
    }
  }
  const lineStart = text.lastIndexOf('\n', index - 1) + 1;
  const lineEnd = text.indexOf('\n', index);
  const line = text.slice(lineStart, lineEnd < 0 ? undefined : lineEnd);
  const relative = index - lineStart;
  // Keep clause-local attribution; a citation from an adjacent statement must
  // not license a different site's number or a second committed quantity.
  const before = line.slice(0, relative);
  const after = line.slice(relative + raw.length);
  const period = before.lastIndexOf('. ');
  const start = Math.max(period < 0 ? 0 : period + 2, before.lastIndexOf(';') + 1);
  const endMatch = after.match(/[.!?](?=\s|$)|;/);
  const end = endMatch?.index == null ? line.length : relative + raw.length + endMatch.index + 1;
  const clause = line.slice(start, end);
  const sentenceStart = period < 0 ? 0 : period + 2;
  const sentenceEndMatch = after.match(/[.!?](?=\s|$)|\|/);
  const sentenceEnd = sentenceEndMatch?.index == null ? line.length : relative + raw.length + sentenceEndMatch.index + 1;
  const sentence = line.slice(sentenceStart, sentenceEnd);
  const isMilestoneScheduleList = unit === 'MW' && (
    (sentence.match(/\b\d[\d,]*(?:\.\d+)?(?:\s*[-–]\s*\d[\d,]*(?:\.\d+)?)?\s*MW\b[^.;!?\n]{0,40}\b(?:Q[1-4]\s*20\d{2}|20\d{2})\b/gi)?.length ?? 0) >= 2
    || (sentence.match(/\b(?:phase\s*\d+|construction(?:\s+power)?)\b[^.;!?\n]{0,50}\b\d[\d,]*(?:\.\d+)?\s*MW\b/gi)?.length ?? 0) >= 2
  );
  const ids = proseRefIds(clause);
  if (ids.length === 0 && isMilestoneScheduleList) {
    ids.push(...proseRefIds(sentence));
  }
  const phaseClaim = phaseClaimEvidenceAt(text, index, evidence);
  ids.push(...(phaseClaim?.refIds ?? []));
  let isEvidenceQuote = false;
  for (const excerpt of evidence.excerpts) {
    const quoteStart = line.indexOf(excerpt.text);
    if (quoteStart >= 0 && relative >= quoteStart && relative + raw.length <= quoteStart + excerpt.text.length) {
      ids.push(...excerpt.refIds); isEvidenceQuote = true;
    }
  }
  const sources = [...new Set(ids)].flatMap(id => evidence.refs.get(id) ?? []);
  if (!sources.length) return false;
  const maskedLine = line
    .replace(/\bwhether\b[^.!?\n]{0,160}\b(?:net\s+IT(?:\s+load)?|IT\s+load|gross\s+facility(?:\s+load)?|facility\s+load)\b[^.!?\n]{0,90}\b(?:unconfirmed|not\s+confirmed|unspecified|not\s+specified|unclear)\b/gi, m => ' '.repeat(m.length))
    .replace(/\b(?:unconfirmed|unspecified|unclear)\s+(?:whether\s+)?[^.!?\n]{0,110}\b(?:IT|net\s+IT|facility|gross\s+facility)\b[^.!?\n]{0,50}\b(?:load|basis)\b/gi, m => ' '.repeat(m.length))
    .replace(/\b(?:IT|net\s+IT)\s*(?:\/|\s+or\s+|\s+vs\.?\s+)\s*(?:gross\s+)?facility(?:\s+load)?(?:\s+basis)?\s+(?:is\s+|remains\s+)?(?:unconfirmed|not\s+confirmed|unspecified)\b/gi, m => ' '.repeat(m.length))
    .replace(/\b(?:not|never)\s+(?:committed|secured|guaranteed|approved|confirmed|available|firm|commissioned|delivered|operating|operational|energized|energised)\b/gi, m => ' '.repeat(m.length));
  const local = maskedLine.slice(Math.max(start, relative - 95), Math.min(end, relative + raw.length + 95));
  if (/\b(?:committed|secured|guaranteed|approved|confirmed|available|firm|commissioned|delivered|operating|operational|energized|energised)\b/i.test(local)
    || /\b(?:is|are|was|were|now|already)\s+(?:fully\s+)?online\b|\b(?:brought|placed|put)\b[^.;]{0,50}\bonline\b/i.test(local)
    || (unit !== 'MW' && /\b(?:IT\s+load|declared\s+(?:project\s+)?(?:total|load))\b/i.test(local))) return false;
  if (unit === 'MW') {
    const quantityNumber = (number: number) => String(number).replace(/\./g, '\\.');
    // A cited battery power rating does not become campus load when the
    // author omits its separate MWh rating. Bind the number directly to BESS
    // and match that role in raw source text, including any named phase.
    const batteryPower = /^\s+(?:BESS|battery\s+(?:energy\s+)?storage(?:\s+system)?)\b/i.test(after);
    if (batteryPower && (args.rangeLow == null || args.rangeLow === value)) {
      const sourcePower = new RegExp(`\\b${quantityNumber(value)}\\s*MW\\b(?:\\s*\\/\\s*\\d+(?:\\.\\d+)?\\s*MWh)?\\s+(?:BESS|battery\\s+(?:energy\\s+)?storage(?:\\s+system)?)\\b`, 'gi');
      const phase = /\bphase\s*(\d+)\b/i.exec(clause)?.[1] ?? (phaseClaim ? String(phaseClaim.phaseSequence) : undefined);
      return sources.some(source => source.kind === 'document' && [...normalize(source.text).matchAll(sourcePower)].some(match => {
        const prior = normalize(source.text).slice(0, match.index);
        const sourcePhase = [...prior.matchAll(/\bphase\s*(\d+)\b/gi)].at(-1)?.[1];
        return phase == null || phase === sourcePhase;
      }));
    }
    if (/\b(?:IT\s+load|declared\s+(?:project\s+)?(?:total|load))\b/i.test(local)) return false;
    // A document-bound battery power/energy pair is equipment scope, not the
    // campus's declared load. Require both values in one source passage.
    const pairAfter = /^\s*\/\s*(\d[\d,]*(?:\.\d+)?)\s*MWh\b/i.exec(after);
    const batteryExample = /\bBESS\s+(?:in\s+(?:each|every)\s+phase\s+)?\(\s*e\.g\.,?\s*$/i.test(before.slice(start))
      && /^\s+for\s+Phase\s+\d+\s*\)/i.test(after.slice(pairAfter?.[0].length ?? 0));
    const batteryPair = pairAfter && (batteryExample || /^\s*\/\s*\d[\d,]*(?:\.\d+)?\s*MWh\b[^.!?\n]{0,30}\bBESS\b/i.test(after)) ? pairAfter : null;
    if (batteryPair && (args.rangeLow == null || args.rangeLow === value)) {
      const energy = Number(batteryPair[1]!.replace(/,/g, ''));
      const pair = new RegExp(`\\b${quantityNumber(value)}\\s*MW\\b[^.!?\\n]{0,35}\\b${quantityNumber(energy)}\\s*MWh\\b`, 'gi');
      const phase = /\bphase\s*(\d+)\b/i.exec(clause)?.[1] ?? (phaseClaim ? String(phaseClaim.phaseSequence) : undefined);
      return sources.some(source => source.kind === 'document' && [...normalize(source.text).matchAll(pair)].some(match => {
        const sourceText = normalize(source.text);
        const passage = sourceText.slice(Math.max(0, match.index - 70), match.index + match[0].length + 70);
        const sourcePhase = [...sourceText.slice(0, match.index).matchAll(/\bphase\s*(\d+)\b/gi)].at(-1)?.[1];
        return /\b(?:BESS|battery\s+(?:energy\s+)?storage)\b/i.test(passage)
          && (phase == null || phase === sourcePhase);
      }));
    }
    // Construction service is a separate, source-requested load. Both ends of
    // a range and its requested date must belong to the same source row.
    const beforeInClause = line.slice(start, relative);
    const constructionBefore = /\bconstruction(?:\s+power)?(?:\s+of|\s+needed|\s+estimated)?\s*[:(]?\s*(?:(\d+(?:\.\d+)?)\s*[-–]\s*)?$/i.exec(beforeInClause);
    const constructionAfter = /\b(?:requested|estimated|planned|staged)\s*(?:(\d+(?:\.\d+)?)\s*[-–]\s*)?$/i.exec(beforeInClause)
      ?? (isMilestoneScheduleList ? /(?:^|[\s,;(])(?:(\d+(?:\.\d+)?)\s*[-–]\s*)?$/i.exec(beforeInClause) : null);
    const isConstructionRole = (constructionBefore != null
      && (isMilestoneScheduleList || /\b(?:requested|estimated|planned|staged|questionnaire|sheet1|b36)\b/i.test(sentence)))
      || (constructionAfter != null && /^\s+(?:of\s+)?construction\s+power\b/i.test(after));
    if (isConstructionRole) {
      // The scalar scanner can match only the upper endpoint of "5-10 MW".
      // Recover its adjacent lower endpoint, never an unrelated prior number.
      const lowerGroup = constructionBefore?.[1] ?? constructionAfter?.[1];
      const lower = lowerGroup == null ? args.rangeLow : Number(lowerGroup);
      const range = lower != null && lower !== value;
      const quantity = new RegExp(`(?<![\\d.\\-–])\\b${range ? `${quantityNumber(lower)}\\s*[-–]\\s*` : ''}${quantityNumber(value)}\\s*MW\\b`, 'i');
      const dates = clause.match(/\bQ[1-4]\s+20\d{2}\b|\b20\d{2}\b/gi) ?? [];
      return sources.some(source => source.kind === 'document' && source.text.split(/\r?\n|\\r\\n|\\n/).some(row =>
        /\bconstruction\s+power\b/i.test(row) && quantity.test(normalize(row))
        && dates.every(date => normalize(row).includes(normalize(date)))));
    }
    if (args.rangeLow != null && args.rangeLow !== value) return false;
    // An attributed preliminary engineering power-block proposal is equipment
    // scope, not campus demand. Require that exact role/value in its cited raw
    // document; the proposal never establishes guaranteed or delivered output.
    const engineeringProposal = /\b(?:developer|owner|applicant)(?:['’]s)?\s+preliminary\s+engineering\s+scope\s+proposes?\s+(?:(?:the\s+)?(?:construction|development)\s+of\s+)?(?:an?\s+)?$/i.test(line.slice(start, relative));
    if (engineeringProposal && /^\s+power\s+block\b/i.test(after)) {
      const quantity = new RegExp(`\\b${quantityNumber(value)}\\s*MW\\s+power\\s+block\\b`, 'i');
      return sources.some(source => source.kind === 'document'
        && /\bpreliminary\s+engineering\s+scope\b/i.test(normalize(source.text))
        && quantity.test(normalize(source.text)));
    }
    const targetSupplyBlock = /\bpreliminary\s+engineering\s+scope\s+for\s+phase\s+(\d+)\s+proposes\b[^.;!?]{0,180}\bto\s+support\s+a\s*$/i.exec(line.slice(start, relative));
    if (targetSupplyBlock && /^\s+target\s+supply\s+block\b/i.test(after)) {
      const amount = new RegExp(`\\b${quantityNumber(value)}\\s*MW\\b`, 'gi');
      return sources.some(source => source.kind === 'document' && /\bpreliminary\s+engineering\s+scope\b/i.test(source.text)
        && [...normalize(source.text).matchAll(amount)].some(match => {
          const rawSource = normalize(source.text);
          const sourcePhase = [...rawSource.slice(0, match.index).matchAll(/\bphase\s+(\d+)\b/gi)].at(-1)?.[1];
          return sourcePhase === targetSupplyBlock[1]
            && /\b(?:guaranteed|power block|target supply block)\b/i.test(rawSource.slice(match.index + match[0].length, match.index + match[0].length + 45));
        }));
    }
    // A cited preliminary generation estimate has its own equipment scope.
    // Match the same quantity and generation role in raw source text; this
    // does not validate the estimate's price or the campus's declared load.
    const indicativeEstimate = /\b(?:indicative|preliminary|non[- ]binding)\b[^.!?;]{0,100}\b(?:estimate|proposal|quote|pricing|price|cost)\b/i.test(clause);
    const generationAfter = /^\s+(?:(?:islanded|BTM|gas[- ]fired|gas|generation|power)\s+){1,5}(?:plant|facility)\b/i.test(after);
    // The same cited estimate described as a generation SCENARIO ("an
    // islanded 150 MW scenario"). The report must itself frame the scenario
    // as generation/islanded, so a bare "150 MW scenario" (which could be a
    // load case) is not admitted.
    const scenarioAfter = /^\s+(?:(?:islanded|BTM|gas[- ]fired|gas|generation|power)\s+){0,3}(?:scenario|case|configuration|option)\b/i.exec(after);
    const scenarioRole = scenarioAfter && (
      /\b(?:islanded|BTM|behind[- ]the[- ]meter|gas[- ]fired|generation)\s+$/i.exec(line.slice(start, relative))?.[0]
      ?? /\b(?:islanded|BTM|gas[- ]fired|generation)\b/i.exec(scenarioAfter[0])?.[0]);
    if (indicativeEstimate && (generationAfter || scenarioRole)) {
      const quantity = new RegExp(`\\b${quantityNumber(value)}\\s*MW\\b`, 'gi');
      const supported = sources.some(source => {
        if (source.kind !== 'document') return false;
        const rawSource = normalize(source.text);
        // An "islanded" characterization must itself be in the cited source.
        if (scenarioRole && !generationAfter && /islanded/i.test(scenarioRole) && !/\bisland(?:ed|ing)\b/i.test(rawSource)) return false;
        // The source must hold the SAME MW as a preliminary/indicative
        // generation facility — unchanged for both report phrasings.
        return [...rawSource.matchAll(quantity)].some(match => {
          const sourceBefore = rawSource.slice(Math.max(0, match.index - 260), match.index);
          const sourceAfter = rawSource.slice(match.index + match[0].length, match.index + match[0].length + 100);
          return /\b(?:preliminary|indicative|non[- ]binding)\b/i.test(sourceBefore)
            && /^\s+(?:(?:islanded|BTM|gas[- ]fired|gas|generation|power)\s+){1,5}(?:plant|facility)\b/i.test(sourceAfter);
        });
      });
      if (supported) return true;
    }
    const subject = line.slice(Math.max(start, relative - 100), relative);
    const numberedPhase = subject.match(/\bphase\s*(\d+)\s*(?:[:(\-–—]|\(\s*\+\s*\d[\d,]*(?:\.\d+)?\s*MW\s*=\s*)?\s*$/i)?.[1]
      ?? /^\s*\)?\s*(?:for\s+|in\s+)?phase\s*(\d+)\b/i.exec(after)?.[1];
    const firstPhase = /\b(?:first|initial)\s+(?:(?:phase|block)\s+(?:of\s+)?)?$|\binitial\s+block\s+of\s+capacity\s*\(\s*e\.g\.,\s*$/i.test(subject);
    const ramp = /\bphased?\s+ramp[- ]?up\s+to\s*$/i.test(subject);
    const datedMilestone = (isMilestoneScheduleList || /\b(?:milestones?|sheet1|b36)\b/i.test(sentence))
      && (/^\s*(?:[:,(\-–—]|\bin\b|\bby\b|\bfor\b|\btarget(?:ed)?\b)*\s*Q[1-4]\s*20\d{2}\b/i.test(after)
        || /\bQ[1-4]\s*20\d{2}\s*[:,(\-–—]?\s*$/i.test(subject));
    const phase = !!numberedPhase || firstPhase || ramp || datedMilestone;
    const contextScope = isMilestoneScheduleList ? sentence : clause;
    // An explicitly planned construction sequence with an exact document
    // citation also attributes a phase. It never asserts delivered capacity.
    const constructionPlan = /\bconstruction\s+will\s+follow\s+the\s+phased\s+ramp[- ]?up\b/i.test(contextScope);
    const attributed = isEvidenceQuote || constructionPlan || /\b(?:developer|customer|questionnaire|sheet1|b36)\b/i.test(contextScope);
    const requested = isEvidenceQuote || /\b(?:requested|proposed|planned|anticipated|targeted)\b/i.test(contextScope)
      || (isMilestoneScheduleList && /\b(?:staged|milestones?|ramp[- ]?up|schedule|phased?|questionnaire|sheet1|b36)\b/i.test(sentence))
      || constructionPlan
      || /\bquestionnaire\s+(?:outlines|records|describes|lists|states)\s+(?:a\s+)?(?:phased?\s+ramp[- ]?up|staged\s+milestones?|load[- ]?ramp)\b/i.test(contextScope)
      || (/\bwill\s+be\s+phased\b/i.test(contextScope)
        && /\bas\s+described\s+in\s+the\s+developer['’]s\s+ramp[- ]?up\s+schedule\b/i.test(contextScope));
    if (!phase || !attributed || !requested) return false;
    const phaseId = numberedPhase ?? (firstPhase ? '1' : undefined);
    // A following phase's date must not be compared with the first phase's
    // quantity. That later phase is checked separately when it has a number.
    const itemBefore = [...subject.matchAll(/,\s*(?=(?:phase\s*\d+|\d[\d,]*(?:\.\d+)?\s*MW)\b)/gi)].at(-1);
    const scopedBefore = itemBefore?.index != null ? subject.slice(itemBefore.index + itemBefore[0].length) : subject;
    const afterInClause = line.slice(relative + raw.length, Math.min(end, relative + raw.length + 95));
    const scopedAfter = afterInClause
      .split(/,\s*with\s+subsequent\s+phases\b|,\s*(?:and\s+)?(?=(?:phase\s*\d+|construction(?:\s+power)?|\d[\d,]*(?:\.\d+)?\s*MW)\b)/i)[0]!;
    const phaseLocal = `${scopedBefore}${raw}${scopedAfter}`;
    const dates = phaseLocal.match(/\bQ[1-4]\s+20\d{2}\b|\b20\d{2}\b/gi) ?? [];
    // A documented phase must carry the same quantity, and any quoted quarter
    // must occur in its source phase line. Mere occurrence elsewhere is insufficient.
    const quantity = new RegExp(`\\b${String(value).replace(/\./g, '\\.')}\\s*MW\\b`, 'i');
    return sources.some(source => source.kind === 'document' && source.text.split(/\r?\n|\\r\\n|\\n/).some(row => {
      const normalized = normalize(row);
      return (phaseId ? new RegExp(`\\bphase\\s*${phaseId}\\b`, 'i') : /\bphase\s*\d+\b/i).test(row) && quantity.test(normalized)
        && dates.every(date => normalized.includes(normalize(date)));
    }));
  }
  if (args.rangeLow != null && args.rangeLow !== value) return false;
  // The source is a generation/storage cohort statistic, explicitly distinguished
  // from a load timetable. National/regional adjectives alone are insufficient.
  // Two report phrasings are admitted, both clause-local and both requiring
  // the cited CURRENT knowledge source below: the legacy "median generator
  // queue time is N years … not a direct analog for load", and the source's
  // own form — "a median of N years … for generation (and storage) projects …
  // not a (data-center) load-connection timetable".
  const legacyForm = /\bmedian\s+generator\s+queue\s+time\s+(?:is|was|of)\s*$/i.test(line.slice(start, relative))
    && /\bnot\s+(?:a\s+)?direct\s+analog\s+for\s+load\b/i.test(clause);
  const sourceFaithfulForm = /\b(?:a|the)\s+median\s+of\s+(?:(?:about|approximately|roughly)\s+)?$/i.test(line.slice(start, relative))
    && /\b(?:generation(?:\s+and\s+storage)?\s+projects?|generators?)\b/i.test(clause)
    && /\bnot\s+(?:a\s+)?(?:(?:data[- ]cent(?:er|re)|large[- ])\s*)?load[- ]connection\s+timetable\b/i.test(clause);
  if (!legacyForm && !sourceFaithfulForm) return false;
  const words = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten'];
  const quantity = new RegExp(`\\bmedian\\s+of\\s+(?:${value}${Number.isInteger(value) && words[value] ? `|${words[value]}` : ''})\\s+years?\\b`, 'i');
  return sources.some(source => source.kind === 'knowledge' && quantity.test(source.text)
    && /transmission-connected generation and storage/i.test(source.text)
    && /not a data-center load-connection timetable/i.test(source.text));
}

/** Frozen grid screening evidence; never a utility service commitment. */
interface FrozenGridEvidence {
  source?: string;
  retrievedAt?: string;
  data?: { estimatedQueueTime?: string | null; estimatedQueueTimeBasis?: string; utilityName?: string | null } | null;
}

type SourceMention = {
  text: string; index: number; raw: string; value: number; rangeLow?: number;
  evidence?: NumericSourceEvidence;
};

/** Same hard-line, sentence and semicolon scope used by source-role binding. */
function sourceMentionClause(args: SourceMention): { clause: string; before: string; after: string } {
  const lineStart = args.text.lastIndexOf('\n', args.index - 1) + 1;
  const lineEnd = args.text.indexOf('\n', args.index);
  const line = args.text.slice(lineStart, lineEnd < 0 ? undefined : lineEnd);
  const relative = args.index - lineStart;
  const prefix = line.slice(0, relative);
  const suffix = line.slice(relative + args.raw.length);
  const period = prefix.lastIndexOf('. ');
  const start = Math.max(period < 0 ? 0 : period + 2, prefix.lastIndexOf(';') + 1);
  const stop = /[.!?](?=\s|$)|;/.exec(suffix);
  const end = stop?.index == null ? line.length : relative + args.raw.length + stop.index + 1;
  return { clause: line.slice(start, end), before: line.slice(start, relative), after: line.slice(relative + args.raw.length, end) };
}

function durationRanges(text: string): Array<{ lowYears: number; highYears: number; raw: string; index: number }> {
  return [...text.matchAll(/\b(\d+(?:\.\d+)?)\s*(?:to|[-–—])\s*(\d+(?:\.\d+)?)\s*(months?|years?|yrs?)\b/gi)]
    .flatMap(match => {
      const divisor = /^month/i.test(match[3]!) ? 12 : 1;
      const lowYears = Number(match[1]) / divisor;
      const highYears = Number(match[2]) / divisor;
      return lowYears > 0 && highYears >= lowYears ? [{ lowYears, highYears, raw: match[0], index: match.index! }] : [];
    });
}

function buildContextOnlyQueueEstimate(grid: FrozenGridEvidence | null | undefined,
  references: Parameters<typeof buildNumericSourceEvidence>[0]['references']): NumericSourceEvidence['contextOnlyQueueEstimate'] {
  if (grid?.data?.estimatedQueueTimeBasis !== 'proximity-heuristic' || !grid.source || !grid.retrievedAt) return undefined;
  const ranges = durationRanges(grid.data.estimatedQueueTime ?? '');
  if (ranges.length !== 1) return undefined;
  const range = ranges[0]!;
  const refIds = (references ?? []).flatMap((ref, index) => ref.field === 'grid'
    && ref.source === grid.source && ref.retrievedAt === grid.retrievedAt ? [index + 1] : []);
  if (!refIds.length) return undefined;
  return { lowYears: range.lowYears, highYears: range.highYears, label: range.raw, basis: 'proximity-heuristic', refIds };
}

function nearestKv(text: string, index: number): number | undefined {
  const values = [...text.matchAll(/\b(\d+(?:\.\d+)?)\s*kV\b/gi)];
  values.sort((a, b) => Math.abs(a.index! - index) - Math.abs(b.index! - index));
  return values[0] ? Number(values[0][1]) : undefined;
}

function buildDeveloperGridCapacityStatements(claims: readonly unknown[] | undefined): NonNullable<NumericSourceEvidence['developerGridCapacityStatements']> {
  return (claims ?? []).flatMap(value => {
    if (!value || typeof value !== 'object') return [];
    const claim = value as { category?: string; state?: string; sourceSpan?: string; claim?: string };
    if (claim.category !== 'power.grid' || claim.state === 'mis-extracted') return [];
    const span = typeof claim.sourceSpan === 'string' && claim.sourceSpan ? claim.sourceSpan
      : typeof claim.claim === 'string' ? claim.claim : '';
    const rows = [...span.matchAll(/(?:hosting capacity|headroom|available (?:transfer )?capacity)\D{0,20}(\d[\d,]*(?:\.\d+)?)\s*MW\b/gi)];
    return rows.flatMap(match => {
      const valueMw = Number(match[1]!.replace(/,/g, ''));
      const mwAt = match.index! + match[0].lastIndexOf(match[1]!);
      const raw = /^\d[\d,]*(?:\.\d+)?\s*MW\b/i.exec(span.slice(mwAt))?.[0];
      if (!raw) return [];
      const bound = sourceMentionClause({ text: span, index: mwAt, raw, value: valueMw });
      const kvs = new Set([...bound.clause.matchAll(/\b(\d+(?:\.\d+)?)\s*kV\b/gi)].map(kv => Number(kv[1])));
      // A following sentence, or an ambiguous two-asset source clause, may
      // not lend its voltage to this developer capacity statement.
      const assetKv = kvs.size === 1 ? [...kvs][0] : undefined;
      return Number.isFinite(valueMw) && valueMw > 0 && assetKv != null && assetKv > 0 ? [{ valueMw, assetKv, span }] : [];
    });
  });
}

/** Only exact, cited proximity context may be advisory. A population mislabel
 * is returned separately so the caller keeps it blocking with its source role. */
export function contextOnlyQueueHeuristicAt(args: SourceMention): 'context' | 'cohort-mislabel' | undefined {
  const estimate = args.evidence?.contextOnlyQueueEstimate;
  if (!estimate || args.rangeLow == null || args.rangeLow === args.value
    || Math.abs(args.rangeLow - estimate.lowYears) > 1e-9 || Math.abs(args.value - estimate.highYears) > 1e-9) return undefined;
  const { clause, before } = sourceMentionClause(args);
  const wording = clause.replace(/<!--[^]*?-->/g, '');
  const ids = proseRefIds(clause);
  if (!ids.length || ids.some(id => !estimate.refIds.includes(id))
    || [...clause.matchAll(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'))].some(token => /\bverified\b/i.test(token[0]))) return undefined;
  if (!/\b(?:heuristic|screening|proximity|generic|rule[- ]of[- ]thumb)\b/i.test(wording)) return undefined;
  const disclaimer = /\b(?:not\s+(?:a|an|the)\s+|(?:does?\s+not|cannot)\s+(?:constitute|establish|determine|represent)\s+(?:a|an|the)\s+)(?:(?:site-specific|project-specific|load-connection|load-service|site|utility)\s+){1,3}(?:timetable|timeline|schedule|estimate|duration)(?:\s+for\s+(?:this|the)\s+(?:project|site|load))?/i;
  if (!disclaimer.test(wording)) return undefined;
  if (/\b(?:site|project|load)(?:['’]s)?\s+(?:interconnection|queue|energization|timeline|will|is|has|requires)|\b(?:estimated|expected)\b[^.;]{0,45}\b(?:queue|interconnection|energization)\s+(?:duration|time|timeline)\s+(?:is|of|will\s+be)\b/i.test(before)) return undefined;
  const outside = wording.replace(disclaimer, '');
  if (/\b(?:this|the|our)\s+(?:site|project|campus|facility|load)(?:['’]s)?\b[^.;]{0,40}\b(?:queue|interconnection|energi[sz]\w*|schedule|timeline|in-service|COD)\b|\b(?:expected|anticipated|likely|will|should|consistent\s+with)\b[^.;]{0,60}\b(?:energi[sz]\w*|in[- ]service|served|connected|online|timeline|schedule)\b/i.test(outside)) return undefined;
  if (/\b(?:will|shall|expected\s+to|estimated\s+to|is\s+expected\s+to|is\s+expected\s+to\s+hold|expected\s+to\s+hold)\b[^.;]{0,90}\b(?:project|site|load|energi[sz]|hold)|\b(?:project|site|load)\b[^.;]{0,45}\b(?:will|shall|requires|expected|estimated|takes?)\b/i.test(outside)) return undefined;
  // A disclaimer must not authorize a separate project-duration assertion.
  if (/\b(?:so|therefore|thus|but)\b[^.;]{0,80}\b(?:expected|will|energiz|energised|energized|hold)\w*/i.test(outside)) return undefined;
  const populationWording = wording.replace(/\b(?:not|no|never)\s+(?:a\s+)?(?:generation|generator|storage|solar|wind|renewable)\s+(?:projects?|cohort|interconnections?)\b/gi, '');
  const cohort = /\b(?:generation|generator|storage|solar|wind|renewable)\s+(?:projects?|cohort|interconnections?)\b/i.test(populationWording)
    || isGeneratorQueueBenchmarkFraming(populationWording, Math.max(0, populationWording.indexOf(args.raw)));
  // The cohort role remains BLOCKING, never an advisory exemption. Only a
  // sole heuristic/disclaimer assertion may enter the advisory path.
  if (cohort) return 'cohort-mislabel';
  const boundDisclaimer = disclaimer.exec(wording)!;
  const tail = wording.slice(boundDisclaimer.index + boundDisclaimer[0].length)
    .replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'), '');
  return /^[\s.,;:!?]*$/.test(tail) ? 'context' : undefined;
}

/** A range is construction scope only when its cited raw document binds the
 * same range and asset; evidence envelopes cannot supply missing inline refs. */
export function isSourceQuotedDocumentDuration(args: SourceMention): boolean {
  if (!args.evidence || args.rangeLow == null || args.rangeLow === args.value) return false;
  const { clause, before, after } = sourceMentionClause(args);
  const ids = proseRefIds(clause);
  if (!ids.length || ids.some(id => args.evidence!.refs.get(id)?.kind !== 'document')) return false;
  const roleAfter = /^\s+(?:to\s+construct\w*|of\s+construction|for\s+construction)\b/i.exec(after);
  if (!roleAfter && !/\bconstruction\s+(?:time|period|duration)\s+(?:of|is|would\s+be)\s*$/i.test(before)) return false;
  const tail = after.slice(roleAfter?.[0].length ?? 0)
    .replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi'), '').trim();
  // Retain the captured closed comparison to an owner-declared objective;
  // it makes no service promise. All other trailing propositions refuse.
  const ownerObjective = /^,\s*which fundamentally misaligns with the user-declared \d+(?:\.\d+)?\s*[-–—]\s*\d+(?:\.\d+)? year timeline objective\.?$/i;
  if (!/^[.,;:!?]*$/.test(tail) && !ownerObjective.test(tail)) return false;
  if (/\b(?:queue|energi[sz]\w*|in[- ]service|load[- ]service|COD|commercial[- ]operation)\b/i.test(clause)) return false;
  const kvs = [...clause.matchAll(/\b(\d+(?:\.\d+)?)\s*kV\b/gi)].map(match => Number(match[1]));
  const assets = [...clause.matchAll(/\b(campus|facility|station|substation|switching\s+station|transmission|line|interconnection\s+facilities)\b/gi)].map(match => match[1]!.toLowerCase());
  // An unbound interconnection duration is still a queue assertion.
  if (!kvs.length && !assets.length) return false;
  return ids.some(id => {
    const source = args.evidence!.refs.get(id)!;
    const sentences = source.text.split(/(?<=[.!?])\s+|;\s*|\n+/);
    const construction = sentences.flatMap((sentence, index) => /\bconstruct\w*\b/i.test(sentence)
      ? durationRanges(sentence).map(range => ({ ...range, sentence, index })) : []);
    const multipleFigures = new Set(construction.map(range => `${range.lowYears}:${range.highYears}`)).size > 1;
    return construction.some(range => {
      if (Math.abs(range.lowYears - args.rangeLow!) > 1e-9 || Math.abs(range.highYears - args.value) > 1e-9
        || /months?/i.test(range.raw) !== /months?/i.test(args.raw)) return false;
      const context = multipleFigures ? range.sentence : sentences.slice(0, range.index + 1).join(' ').slice(-600);
      // Multi-option documents require the asset in this same duration sentence.
      // The immediately previous sentence is admitted only if it contains no
      // other construction-duration option.
      const previous = sentences[range.index - 1] ?? '';
      const assetContext = multipleFigures && !durationRanges(previous).length ? `${previous} ${context}` : context;
      return kvs.every(kv => [...assetContext.matchAll(/\b(\d+(?:\.\d+)?)\s*kV\b/gi)].some(match => Number(match[1]) === kv))
        && assets.every(asset => assetContext.toLowerCase().includes(asset));
    });
  });
}

/** Developer source-role attribution is neither utility confirmation nor
 * available site capacity. Exact recorded MW and nearest asset kV both bind. */
export function isDeveloperStatedGridCapacity(args: SourceMention): boolean {
  const statements = args.evidence?.developerGridCapacityStatements;
  if (!statements?.length || (args.rangeLow != null && args.rangeLow !== args.value)) return false;
  const { clause, before } = sourceMentionClause(args);
  if (!/\b(?:user[- ]provided|user\s+context|developer|owner|customer|applicant|site[- ]context|unverified\s+context|preliminary\s+GIS\s+screening)\b/i.test(clause)
    || !/\b(?:capacity|headroom|hosting)\b/i.test(clause)
    || /\b(?:firm|guarantee\w*|committed|reserved|allocat\w*|available\s+to\s+(?:the|this)\s+(?:project|site|facility|campus)|will[- ]serve|confirmed|secured|approved|sufficient|adequate|enough|to\s+serve|deliverable|for\s+the\s+project)\b/i.test(clause)) return false;
  const utility = args.evidence?.gridUtilityToken?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const attribution = new RegExp(`\\b(?:utility|utilities|ISO|RTO|transmission\\s+owner|${utility || '(?!)'})\\b[^.;]{0,45}\\b(?:has|have|had|is|are)?\\s*(?:indicated|reported|stated|offered|provided|claims?|claimed|confirmed|verified|approved|committed|allocated|reserved)\\b`, 'i');
  if (attribution.test(clause)) return false;
  const kv = nearestKv(clause, before.length);
  return statements.some(statement => Math.abs(statement.valueMw - args.value) <= 1e-9
    && (kv != null ? kv === statement.assetKv : statements.length === 1));
}
